import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'; // Add GLTFLoader
//import MapScene from './MapScene.js';
import IslandMapScene from './IslandMapScene.js';
import WeaponSystem from './WeaponSystem.js';
import { io } from 'socket.io-client';
//import { markRaw } from 'vue';  // Import markRaw from Vue
import { WEAPON_TYPES } from '../weapons'; // Import weapon types
import GradientSkybox from '../environment/GradientSkybox.js'; // Import GradientSkybox
import PlayerManager from '../managers/PlayerManager.js'; // Import PlayerManager

const MapScene = IslandMapScene;
// Constants for player positioning
const TERRAIN_Y_OFFSET = 0.05; // Height offset from terrain (increased to ensure players are clearly above ground)
const PLAYER_EYE_HEIGHT = 1.6; // Height of camera above player position


// Create a simple markRaw function that returns the object unchanged
// This allows the code to work even if Vue is not available
const markRaw = (obj) => obj;

// Helper function to init non-reactive objects
function initNonReactive(obj) {
  // If using Vue, you can use markRaw to make objects non-reactive
  // For standalone use, we just return the object as-is
  return markRaw(obj);
}

class MainScene {
  constructor(container) {
    const self = initNonReactive(this);
    
    // DOM container for renderer
    self.container = container;
    
    // Initialize Three.js scene
    self.scene = markRaw(new THREE.Scene());
    
    // Animation loop state tracking
    self.isAnimating = false; // Animation state
    self.animationFrame = null; // Animation frame reference
    self.lastTimestamp = 0; // Last animation timestamp
    
    // Weapon system reference
    self.weaponSystem = null;
    
    // Player tracking
    self.localPlayer = null; // Reference to the local player object
    self.players = {}; // All players in the game
    
    // Socket connection
    self.socket = null;
    
    // Game state
    self.mapScene = null; // Will hold the map scene
    self.modelCache = {}; // Cache for loaded models
    
    // Game state
    self.isGameStarted = false; // Flag to track if the match has started
    self.playerJoined = false; // Flag to track if player has joined the game
    
    // Camera animation state
    self.cameraTransitioning = false;
    self.cameraTransitionStart = null;
    self.cameraTransitionDuration = 1500; // 1.5 seconds
    self.cameraTransitionStartPos = new THREE.Vector3();
    self.cameraTransitionEndPos = new THREE.Vector3();
    self.cameraTransitionStartLookAt = new THREE.Vector3();
    self.cameraTransitionEndLookAt = new THREE.Vector3();
    
    // Debug options
    self.showHitboxes = false; // Toggle for hitbox visualization
    self.hitboxHelpers = {}; // Storage for hitbox visualization helpers
    self.debugAnimations = false; // Toggle for animation debugging
    
    // Movement controls
    self.keys = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      run: false,
      jump: false
    };
    
    // Mouse movement for camera rotation
    self.mouse = {
      x: 0,
      y: 0,
      locked: false
    };
    
    // Player attributes
    self.playerVelocity = new THREE.Vector3(0, 0, 0);
    self.playerDirection = new THREE.Vector3(0, 0, 0);
    self.cameraRotation = new THREE.Euler(0, 0, 0, 'YXZ');
    self.moveSpeed = 0.012; // Reduced from 0.1 to 0.05 (50% reduction)
    self.runMultiplier = 1.5;
    self.characterSpeed = 6; // Will be adjusted based on character type
    
    // Initialize crouching variables with proper values
    self.PLAYER_EYE_HEIGHT = 1.6; // Standard eye height for player camera
    self.isCrouching = false;
    self.cameraHeightCurrent = self.PLAYER_EYE_HEIGHT;
    self.cameraHeightTarget = self.PLAYER_EYE_HEIGHT;
    self.lastCrouchToggleTime = 0;
    
    // Initialize jump physics variables
    self.isJumping = false;
    self.jumpVelocity = 0;
    self.jumpStrength = 0.15;
    self.gravity = 0.01;
    self.onGround = true;
    self.canJump = true; // Tracks if space has been released since last jump
    
    self.init();
    self.setupLighting();
    self.createMap(); // Use map scene instead of simple objects
    
    // Initialize character models for collision detection
    self.setupCharacterModels();
    
    // Initialize the PlayerManager after scene and camera setup
    console.log('Initializing PlayerManager with:', 
                'scene:', !!self.scene, 
                'camera:', !!self.camera, 
                'mapScene:', !!self.mapScene);
    self.playerManager = markRaw(new PlayerManager(self.scene, self.camera, self.mapScene));
    
    // Point the players reference to the PlayerManager's players
    self.players = self.playerManager.players;
    
    // Initialize weapon system immediately after the PlayerManager
    // We don't need to wait for the player to be created
    self.initWeaponSystem();
    
    self.setupResizeHandler();
    self.setupSocketConnection();
    self.setupControls(); // Setup keyboard and mouse controls
    
    return self; // Return the non-reactive version
  }
  
  init() {
    // Create scene - already created in constructor
    // Remove the background color as we'll use the skybox instead
    // this.scene.background = new THREE.Color(0x87ceeb); // Sky blue background
    
    // Create and add the gradient skybox
    this.skybox = markRaw(new GradientSkybox({
      topColor: [0.3, 0.4, 0.7],     // Soft blue
  middleColor: [0.7, 0.6, 0.8],  // Lavender purple
  bottomColor: [1.0, 0.8, 0.7],  // Pale peach
  enableHorizonGlow: true,
  horizonGlowColor: [1.0, 0.9, 0.8],
  horizonGlowWidth: 0.15,
  horizonGlowIntensity: 0.4,
  offset: -0.1,
  exponent: 0.8,
  enableNoise: true,
  noiseIntensity: 0.03,
      animate: true
    }));
    this.skybox.create(this.scene);
    
    // Create camera
    this.camera = markRaw(new THREE.PerspectiveCamera(
      75, // Field of view
      window.innerWidth / window.innerHeight, // Aspect ratio
      0.1, // Near clipping plane - changed from default to see closer objects
      1000 // Far clipping plane
    ));
    
    // Initialize with default first-person camera position instead of bird's eye view
    this.camera.position.set(0, this.PLAYER_EYE_HEIGHT, 0);
    this.camera.rotation.set(0, 0, 0);
    
    // Create renderer
    this.renderer = markRaw(new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true // Enable alpha for transparent backgrounds
    }));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true; // Enable shadows
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Use soft shadows for better quality
    
    // Make sure the renderer's DOM element is added to the container
    if (this.container && this.renderer) {
      // Clear any existing content
      while (this.container.firstChild) {
        this.container.removeChild(this.container.firstChild);
      }
      this.container.appendChild(this.renderer.domElement);
      console.log('Added WebGL renderer to container');
    } else {
      console.error('Cannot add renderer to container. Container or renderer is null.');
    }
    
    // Initialize the GLTF loader for 3D models
    this.gltfLoader = markRaw(new GLTFLoader());
    
    // Setup player pool stats logging
    this.setupPoolStatsLogger();
    
    // GPU performance optimizations
    this.initPerformanceMonitoring();
  }
  
  // Initialize performance monitoring and dynamic quality adjustments
  initPerformanceMonitoring() {
    // Performance settings
    this.performanceSettings = {
      // Quality levels for dynamic adjustments
      quality: {
        low: {
          shadowMapSize: 1024,
          shadowDistance: 50,
          pixelRatio: Math.min(1.0, window.devicePixelRatio),
          antialias: false,
          maxVisiblePlayers: 10
        },
        medium: {
          shadowMapSize: 2048,
          shadowDistance: 70, 
          pixelRatio: Math.min(1.5, window.devicePixelRatio),
          antialias: true,
          maxVisiblePlayers: 15
        },
        high: {
          shadowMapSize: 2048,
          shadowDistance: 100,
          pixelRatio: window.devicePixelRatio,
          antialias: true,
          maxVisiblePlayers: 20
        }
      },
      // Performance tracking
      monitoring: {
        enabled: true,
        targetFPS: 60,
        minAcceptableFPS: 30,
        frameTimes: [],
        frameTimeHistorySize: 60, // Track last 60 frames for averaging
        lastQualityAdjustment: 0,
        adjustmentCooldown: 3000 // Only adjust every 3 seconds
      },
      // Current quality level
      currentQuality: 'medium', // Start at medium quality
      // Allow manual override
      manualOverride: false,
      // Enable/disable specific optimizations
      optimizations: {
        frustumCulling: true // Enable frustum culling by default
      }
    };
    
    // Apply initial quality settings
    this.applyQualitySettings(this.performanceSettings.currentQuality);
    
    // Setup FPS counter and performance monitoring
    this.stats = {
      fps: 0,
      frameTime: 0,
      lastFrameTime: performance.now(),
      framesThisSecond: 0,
      lastFpsUpdate: performance.now(),
      visible: false
    };
    
    // Create FPS display element (hidden by default)
    this.createFpsDisplay();
    
    // Add performance hotkeys
    document.addEventListener('keydown', (e) => {
      // Alt+F to toggle FPS display
      if (e.altKey && e.code === 'KeyF') {
        e.preventDefault();
        this.toggleFpsDisplay();
      }
      
      // Alt+1, Alt+2, Alt+3 for manual quality settings
      if (e.altKey) {
        if (e.code === 'Digit1') {
          e.preventDefault();
          this.setQualityLevel('low', true);
        } else if (e.code === 'Digit2') {
          e.preventDefault();
          this.setQualityLevel('medium', true);
        } else if (e.code === 'Digit3') {
          e.preventDefault();
          this.setQualityLevel('high', true);
        } else if (e.code === 'Digit0') {
          e.preventDefault();
          // Toggle manual override of quality settings
          this.performanceSettings.manualOverride = !this.performanceSettings.manualOverride;
          console.log(`Manual quality override: ${this.performanceSettings.manualOverride ? 'Enabled' : 'Disabled'}`);
        } else if (e.code === 'KeyC') {
          // Add hotkey to toggle frustum culling (Alt+C)
          e.preventDefault();
          this.toggleFrustumCulling();
        }
      }
    });
  }
  
  // Add a method to toggle frustum culling for troubleshooting terrain issues
  toggleFrustumCulling() {
    // Toggle the frustum culling setting
    this.performanceSettings.optimizations.frustumCulling = 
      !this.performanceSettings.optimizations.frustumCulling;
    
    // Make everything visible if frustum culling is disabled
    if (!this.performanceSettings.optimizations.frustumCulling) {
      console.log('Frustum culling disabled - all scene objects will be rendered');
      this.scene.traverse(object => {
        // Make all scene meshes visible again
        if (object.isMesh) {
          object.visible = true;
        }
      });
    } else {
      console.log('Frustum culling enabled - distant objects will be culled');
    }
    
    // Update FPS display to show new state
    if (this.fpsDisplay && this.stats.visible) {
      this.updateFpsDisplayText();
    }
    
    return this.performanceSettings.optimizations.frustumCulling;
  }
  
  // Update FPS display to include culling status
  updateFpsDisplayText() {
    if (!this.fpsDisplay) return;
    
    const quality = this.performanceSettings.currentQuality;
    const cullingEnabled = this.performanceSettings.optimizations.frustumCulling ? 'ON' : 'OFF';
    
    this.fpsDisplay.innerHTML = 
      `FPS: ${this.stats.fps} | Frame Time: ${this.stats.frameTime.toFixed(1)}ms | Quality: ${quality} | Culling: ${cullingEnabled}`;
    
    // Add keyboard hint
    if (!this.fpsDisplay.hasAttribute('data-hint-added')) {
      this.fpsDisplay.setAttribute('data-hint-added', 'true');
      
      const hint = document.createElement('div');
      hint.style.fontSize = '10px';
      hint.style.opacity = '0.8';
      hint.style.marginTop = '5px';
      hint.innerHTML = 'Alt+1/2/3: Quality | Alt+C: Toggle Culling | Alt+0: Manual Mode';
      
      this.fpsDisplay.appendChild(hint);
    }
  }
  
  // Helper methods for performance monitoring
  createFpsDisplay() {
    // Create FPS display element
    const fpsDisplay = document.createElement('div');
    fpsDisplay.id = 'fps-display';
    fpsDisplay.style.position = 'fixed';
    fpsDisplay.style.top = '10px';
    fpsDisplay.style.right = '10px';
    fpsDisplay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    fpsDisplay.style.color = 'white';
    fpsDisplay.style.padding = '5px 10px';
    fpsDisplay.style.borderRadius = '5px';
    fpsDisplay.style.fontFamily = 'monospace';
    fpsDisplay.style.zIndex = '9999';
    fpsDisplay.style.display = 'none'; // Hidden by default
    fpsDisplay.innerHTML = 'FPS: 60';
    document.body.appendChild(fpsDisplay);
    
    this.fpsDisplay = fpsDisplay;
  }
  
  toggleFpsDisplay() {
    this.stats.visible = !this.stats.visible;
    if (this.fpsDisplay) {
      this.fpsDisplay.style.display = this.stats.visible ? 'block' : 'none';
    }
  }
  
  updatePerformanceStats() {
    const now = performance.now();
    
    // Update FPS counter
    this.stats.framesThisSecond++;
    
    // Calculate this frame's delta time
    const frameTime = now - this.stats.lastFrameTime;
    this.stats.lastFrameTime = now;
    
    // Add to frame time history (for averaging)
    this.performanceSettings.monitoring.frameTimes.push(frameTime);
    
    // Keep history at specified size
    while (this.performanceSettings.monitoring.frameTimes.length > 
           this.performanceSettings.monitoring.frameTimeHistorySize) {
      this.performanceSettings.monitoring.frameTimes.shift();
    }
    
    // Update FPS display once per second
    if (now - this.stats.lastFpsUpdate >= 1000) {
      // Calculate average FPS
      this.stats.fps = this.stats.framesThisSecond;
      this.stats.framesThisSecond = 0;
      this.stats.lastFpsUpdate = now;
      
      // Calculate average frame time
      if (this.performanceSettings.monitoring.frameTimes.length > 0) {
        const sum = this.performanceSettings.monitoring.frameTimes.reduce((a, b) => a + b, 0);
        this.stats.frameTime = Math.round(sum / this.performanceSettings.monitoring.frameTimes.length);
      }
      
      // Update display if visible
      if (this.stats.visible && this.fpsDisplay) {
        this.updateFpsDisplayText();
      }
      
      // Check if we need to adjust quality
      this.checkPerformance();
    }
  }
  
  checkPerformance() {
    // Skip if manual override is enabled
    if (this.performanceSettings.manualOverride) return;
    
    // Skip if not enough time has passed since last adjustment
    const now = performance.now();
    const timeSinceLastAdjustment = now - this.performanceSettings.monitoring.lastQualityAdjustment;
    if (timeSinceLastAdjustment < this.performanceSettings.monitoring.adjustmentCooldown) {
      return;
    }
    
    // Get current FPS
    const currentFPS = this.stats.fps;
    const targetFPS = this.performanceSettings.monitoring.targetFPS;
    const minFPS = this.performanceSettings.monitoring.minAcceptableFPS;
    
    // Current quality
    const currentQuality = this.performanceSettings.currentQuality;
    
    // Determine if we need to adjust quality
    if (currentFPS < minFPS && currentQuality !== 'low') {
      // FPS too low, decrease quality
      console.log(`FPS too low (${currentFPS}), decreasing quality`);
      this.setQualityLevel(currentQuality === 'high' ? 'medium' : 'low');
      this.performanceSettings.monitoring.lastQualityAdjustment = now;
    } 
    else if (currentFPS >= targetFPS * 1.2 && currentQuality !== 'high') {
      // FPS well above target, can increase quality
      console.log(`FPS well above target (${currentFPS}), increasing quality`);
      this.setQualityLevel(currentQuality === 'low' ? 'medium' : 'high');
      this.performanceSettings.monitoring.lastQualityAdjustment = now;
    }
  }
  
  setQualityLevel(level, isManualOverride) {
    // Available quality levels
    const validLevels = ['low', 'medium', 'high'];
    
    // Validate level
    if (!validLevels.includes(level)) {
      console.error(`Invalid quality level: ${level}`);
      return;
    }
    
    // Set manual override if specified
    if (isManualOverride) {
      this.performanceSettings.manualOverride = true;
    }
    
    // Update current quality
    this.performanceSettings.currentQuality = level;
    
    // Apply settings
    this.applyQualitySettings(level);
    
    console.log(`Quality set to: ${level}${isManualOverride ? ' (manual)' : ''}`);
  }
  
  applyQualitySettings(level) {
    // Get settings for the specified level
    const settings = this.performanceSettings.quality[level];
    if (!settings) return;
    
    // Apply renderer settings
    if (this.renderer) {
      // Update pixel ratio
      this.renderer.setPixelRatio(settings.pixelRatio);
      
      // Update antialiasing (requires renderer recreation)
      if (this.renderer.antialias !== settings.antialias) {
        console.log(`Antialiasing change requires browser refresh to apply`);
        // We can't change this at runtime, so just note it for now
      }
      
      // Update shadow settings
      if (this.renderer.shadowMap.enabled) {
        // Update shadow map size for the directional light
        const directionalLight = this.scene.children.find(c => c.isDirectionalLight);
        if (directionalLight && directionalLight.shadow) {
          directionalLight.shadow.mapSize.width = settings.shadowMapSize;
          directionalLight.shadow.mapSize.height = settings.shadowMapSize;
          directionalLight.shadow.map?.dispose(); // Force shadow map rebuild
          directionalLight.shadow.camera.far = settings.shadowDistance;
          directionalLight.shadow.camera.updateProjectionMatrix();
          
          console.log(`Updated shadow map size to ${settings.shadowMapSize} and distance to ${settings.shadowDistance}`);
        }
      }
    }
    
    // Apply scene-level optimizations
    const maxVisiblePlayers = settings.maxVisiblePlayers;
    if (this.playerManager) {
      this.playerManager.setMaxVisiblePlayers(maxVisiblePlayers);
    }
  }
  
  setupLighting() {
    // Add ambient light
    const ambientLight = markRaw(new THREE.AmbientLight(0x404040, 5.6)); // Soft white light
    this.scene.add(ambientLight);
    
    // Add directional light (like sunlight)
    const directionalLight = markRaw(new THREE.DirectionalLight(0xB299CC, 4.2));
    directionalLight.position.set(10, 20, 10);
    directionalLight.castShadow = true;
    
    // Optimize shadow settings
    directionalLight.shadow.mapSize.width = 2048; // Increased resolution
    directionalLight.shadow.mapSize.height = 2048; // Increased resolution
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 100; // Increased far plane
    directionalLight.shadow.camera.left = -40; // Expanded shadow camera frustum
    directionalLight.shadow.camera.right = 40;
    directionalLight.shadow.camera.top = 40;
    directionalLight.shadow.camera.bottom = -40;
    
    // Improve shadow quality
    directionalLight.shadow.bias = -0.0005; // Reduce shadow acne
    directionalLight.shadow.normalBias = 0.02; // Helps with thin objects
    
    this.scene.add(directionalLight);
    
    // Add a secondary directional light from the opposite direction
    const secondaryLight = markRaw(new THREE.DirectionalLight(0x404040, 4.4));
    secondaryLight.position.set(-10, 10, -10);
    this.scene.add(secondaryLight);
  }
  
  createMap() {
    // Initialize and create the map
    this.mapScene = markRaw(new MapScene());
    this.mapScene.createMap(this.scene);
    
    // Create water boundary visualization if debug mode is enabled
    // This helps see where the water boundary is
    if (this.mapScene && this.mapScene.createWaterBoundaryIndicators) {
      this.waterBoundaryVisualization = this.mapScene.createWaterBoundaryIndicators(this.scene);
      // Hide by default
      if (this.waterBoundaryVisualization) {
        this.waterBoundaryVisualization.visible = false;
      }
    }
    
    // Create a simple cube to represent a player for testing
    const cubeGeometry = markRaw(new THREE.BoxGeometry(1, 1, 1));
    const cubeMaterial = markRaw(new THREE.MeshStandardMaterial({ 
      color: 0x00ff00,  // Green color
      roughness: 0.7,
      metalness: 0.3
    }));
    this.cube = markRaw(new THREE.Mesh(cubeGeometry, cubeMaterial));
    this.cube.position.set(0, 0.5, 0); // Position slightly above the floor
    this.cube.castShadow = true;
    this.scene.add(this.cube);
  }
  
  // Setup character models - REMOVED, now handled by PlayerManager
  
  // Load a GLTF/GLB model - REMOVED, now handled by PlayerManager
  
  // Helper method to properly deep clone a GLTF model - REMOVED, now handled by PlayerManager
  
  // Method to add player representation to the scene with character type
  setupCharacterModels() {
    // Define GLB model configurations for different character types
    this.characterModels = {
      soldier: {
        modelPath: '/models/players/soldier.glb',
        height: 1.2,
        scale: 1.0,
        speed: 6,  // Standard speed for Soldier
        animations: {
          idle: 'rifle_idle',
          walk: 'rifle_run_fwd', // Use rifle forward as walk
          run: 'rifle_run_fwd',  // Use same animation for run but will be played faster
          walkBackward: 'rifle_run_bwd',
          walkLeft: 'rifle_run_left',
          walkRight: 'rifle_run_right',
          death: 'dying',
          jump: 'rifle_idle',  // Use idle animation for jump (as placeholder)
          fall: 'rifle_idle'   // Use idle animation for fall (as placeholder)
        }
      },
      scout: {
        modelPath: '/models/players/scout.glb',
        height: 1.3,
        scale: 0.9,
        speed: 9,  // Faster speed for Scout
        animations: {
          idle: 'rifle_idle',
          walk: 'rifle_run_fwd',
          run: 'rifle_run_fwd',
          walkBackward: 'rifle_run_bwd',
          walkLeft: 'rifle_run_left',
          walkRight: 'rifle_run_right',
          death: 'dying',
          jump: 'rifle_idle',  // Use idle animation for jump (as placeholder) 
          fall: 'rifle_idle'   // Use idle animation for fall (as placeholder)
        }
      }
    };
    
    // Preload models
    Object.keys(this.characterModels).forEach(characterType => {
      this.loadModel(this.characterModels[characterType].modelPath);
    });
  }
  
  // Load a GLTF/GLB model and store in cache
  loadModel(modelPath) {
    // Return from cache if already loaded
    if (this.modelCache[modelPath]) {
      return Promise.resolve(this.modelCache[modelPath]);
    }
    
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        modelPath,
        (gltf) => {
          // Mark the model as non-reactive (for Vue)
          const model = markRaw(gltf);
          console.log(model.animations)
          // Setup model for shadowing
          model.scene.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = true;
              
              // Apply polygon offset to fix shadows when objects intersect with terrain
              if (child.material) {
                // Ensure we have a unique material instance
                if (!child.material._isCloned) {
                  if (Array.isArray(child.material)) {
                    child.material = child.material.map(m => m.clone());
                    child.material.forEach(m => m._isCloned = true);
                  } else {
                    child.material = child.material.clone();
                    child.material._isCloned = true;
                  }
                }
                
                // Apply polygon offset to the material(s)
                if (Array.isArray(child.material)) {
                  child.material.forEach(material => {
                    material.polygonOffset = true;
                    material.polygonOffsetFactor = -0.2;
                    material.polygonOffsetUnits = -1;
                  });
                } else {
                  child.material.polygonOffset = true;
                  child.material.polygonOffsetFactor = -0.2;
                  child.material.polygonOffsetUnits = -1;
                }
              }
            }
          });
          
          // Store in cache
          this.modelCache[modelPath] = model;
          console.log(`Model loaded and cached: ${modelPath}`);
          resolve(model);
        },
        (xhr) => {
          console.log(`${modelPath} loading: ${(xhr.loaded / xhr.total * 100).toFixed(2)}% loaded`);
        },
        (error) => {
          console.error(`Error loading model ${modelPath}:`, error);
          reject(error);
        }
      );
    });
  }
  
  // Helper method to properly deep clone a GLTF model with all transformations
  deepCloneGltf(gltf) {
    // Create a new clone object with the same structure
    const clone = {
      animations: gltf.animations,
      scene: new THREE.Group()
    };
    
    // Map to track original objects to their clones for references
    const cloneMap = new Map();
    
    // Clone all the child objects properly
    gltf.scene.traverse((originalObj) => {
      let clonedObj;
      
      // Clone different object types appropriately
      if (originalObj.isSkinnedMesh) {
        // For skinned meshes, we need to clone geometry and material
        const geometry = originalObj.geometry.clone();
        const material = originalObj.material.clone();
        clonedObj = new THREE.SkinnedMesh(geometry, material);
        
        // Copy bind matrix and mode
        clonedObj.bindMatrix.copy(originalObj.bindMatrix);
        clonedObj.bindMode = originalObj.bindMode;
        
        // We'll handle the skeleton after all objects are cloned
        cloneMap.set(originalObj, clonedObj);
      } 
      else if (originalObj.isMesh) {
        // Regular meshes
        const geometry = originalObj.geometry.clone();
        const material = originalObj.material.clone();
        clonedObj = new THREE.Mesh(geometry, material);
        cloneMap.set(originalObj, clonedObj);
      }
      else if (originalObj.isGroup) {
        // Groups
        clonedObj = new THREE.Group();
        cloneMap.set(originalObj, clonedObj);
      }
      else if (originalObj.isBone) {
        // Bones
        clonedObj = new THREE.Bone();
        cloneMap.set(originalObj, clonedObj);
      }
      else {
        // Other object types (like empty nodes)
        clonedObj = new THREE.Object3D();
        cloneMap.set(originalObj, clonedObj);
      }
      
      // Copy the transformation properties
      if (clonedObj && originalObj) {
        clonedObj.position.copy(originalObj.position);
        clonedObj.quaternion.copy(originalObj.quaternion);
        clonedObj.scale.copy(originalObj.scale);
        clonedObj.matrix.copy(originalObj.matrix);
        clonedObj.matrixWorld.copy(originalObj.matrixWorld);
        clonedObj.matrixAutoUpdate = originalObj.matrixAutoUpdate;
        clonedObj.matrixWorldNeedsUpdate = originalObj.matrixWorldNeedsUpdate;
        
        // Copy the name and userData
        clonedObj.name = originalObj.name;
        clonedObj.userData = JSON.parse(JSON.stringify(originalObj.userData));
        
        // Copy visibility and other basic properties
        clonedObj.visible = originalObj.visible;
        clonedObj.castShadow = originalObj.castShadow;
        clonedObj.receiveShadow = originalObj.receiveShadow;
      }
    });
    
    // Recreate the hierarchy
    gltf.scene.traverse((originalObj) => {
      const clonedObj = cloneMap.get(originalObj);
      
      if (clonedObj && originalObj !== gltf.scene) {
        // Find the parent's clone and add this object as a child
        const originalParent = originalObj.parent;
        if (originalParent) {
          const clonedParent = cloneMap.get(originalParent);
          if (clonedParent) {
            clonedParent.add(clonedObj);
          }
        }
      }
    });
    
    // Set up skeletons and bones
    gltf.scene.traverse((originalObj) => {
      if (originalObj.isSkinnedMesh) {
        const clonedMesh = cloneMap.get(originalObj);
        const originalSkeleton = originalObj.skeleton;
        
        // Create a new skeleton
        const bones = [];
        const boneInverses = [];
        
        // Map the bones
        for (let i = 0; i < originalSkeleton.bones.length; i++) {
          const originalBone = originalSkeleton.bones[i];
          const clonedBone = cloneMap.get(originalBone);
          
          if (clonedBone) {
            bones.push(clonedBone);
            boneInverses.push(originalSkeleton.boneInverses[i].clone());
          }
        }
        
        // Create and assign the new skeleton
        clonedMesh.skeleton = new THREE.Skeleton(bones, boneInverses);
      }
    });
    
    // Build the root of the clone
    gltf.scene.children.forEach((child) => {
      const clonedChild = cloneMap.get(child);
      if (clonedChild) {
        clone.scene.add(clonedChild);
      }
    });
    
    // Make sure the scene is marked as non-reactive (for Vue)
    clone.scene = markRaw(clone.scene);
    
    return clone;
  }
  
  setupResizeHandler() {
    // Handle window resize
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
  }
  
  setupSocketConnection() {
    try {
      // Use the environment variable for server URL if available, with fallbacks
      const serverUrl = import.meta.env.VITE_SERVER_URL || 
        (process.env.NODE_ENV === 'production' 
          ? window.location.origin 
          : 'http://localhost:3000');
      
      // Create the Socket.IO client and connect to the server
      // Set reconnection attempts and timeout values
      this.socket = io(serverUrl, {
        reconnectionAttempts: 5,
        timeout: 10000,
        transports: ['websocket', 'polling'] // Try WebSocket first, fall back to polling
      });
      
      // Handle connection established
      this.socket.on('connect', () => {
        console.log('Connected to game server with socket ID:', this.socket.id);
        
        // Update weapon system with connected socket if it exists
        if (this.weaponSystem) {
          this.updateWeaponSystemSocket();
        }
      });
      
      // Handle welcome message from server
      this.socket.on('welcome', (data) => {
        console.log('Received welcome message:', data.message);
        console.log('Server assigned socket ID:', data.socketId || data.yourId); // Handle different property names
        console.log('Current player statistics:', data.playerStats);
        
        // If we already have player data, send it to the server
        if (this.playerData) {
          this.socket.emit('join-game', this.playerData);
        }
        
        // Dispatch a player-stats-update event for GameView
        const statsUpdateEvent = new CustomEvent('player-stats-update', {
          detail: data.playerStats
        });
        window.dispatchEvent(statsUpdateEvent);
      });
      
      // Add player stats update handler
      this.socket.on('player-stats-update', (stats) => {
        console.log('Received player stats update:', stats);
        
        // Dispatch a custom event for GameView to update its player count display
        const statsUpdateEvent = new CustomEvent('player-stats-update', {
          detail: stats
        });
        window.dispatchEvent(statsUpdateEvent);
        
        // Request updated player list to ensure we have all players
        this.socket.emit('request-existing-players');
      });
      
      // Handle connection errors
      this.socket.on('connect_error', (error) => {
        console.error('Socket.IO connection error:', error);
        // Continue with a local experience if can't connect to the server
        this.handleSocketConnectionFailure();
      });
      
      // Add the rest of the event handlers
      this.setupSocketEventHandlers();
    } catch (error) {
      console.error('Error setting up Socket.IO connection:', error);
      this.handleSocketConnectionFailure();
    }
  }
  
  // Update WeaponSystem with the connected socket
  updateWeaponSystemSocket() {
    if (this.weaponSystem && this.socket && this.socket.connected) {
      console.log(`Updating WeaponSystem with connected socket (ID: ${this.socket.id})`);
      // Update socket reference
      this.weaponSystem.socket = this.socket;
      // Update local player ID
      this.weaponSystem.localPlayerId = this.socket.id;
      
      console.log('WeaponSystem socket reference updated successfully');
    } else {
      console.warn('Cannot update WeaponSystem socket: Either WeaponSystem not initialized or socket not connected');
    }
  }
  
  setupSocketEventHandlers() {
    if (!this.socket) return;
    
    // Handle successful game join
    this.socket.on('game-joined', (data) => {
      console.log('Game joined confirmation received:', data);
      
      // Store character data
      this.characterData = data.characterData;
      
      // Initialize health based on character
      this.playerHealth = data.health || 100;
      
      // Set game mode
      this.gameMode = data.gameMode;
      
      // Create the local player with server-provided position
      if (data.position) {
        console.log('Using server-provided spawn position:', data.position);
        this.createPlayer({
          id: this.socket.id,
          character: data.character || 'soldier',
          team: 'none', // FFA mode has no teams
          position: data.position // Use server-provided position
        });
      } else {
        // Fallback to default position if server doesn't provide one
        console.warn('No spawn position provided by server, using default');
        this.createPlayer({
          id: this.socket.id,
          character: data.character || 'soldier',
          team: 'none' // FFA mode has no teams
        });
      }
      
      // Start camera transition to player view
      if (this.localPlayer) {
        this.startCameraTransition(this.localPlayer.position);
      }
      
      // Set playerJoined flag
      this.playerJoined = true;
      
      // Initialize weapon system after player is created
      this.initWeaponSystem();
      
      // Dispatch event for game state management
      const joinedEvent = new CustomEvent('game-joined', { 
        detail: { 
          character: data.character,
          health: this.playerHealth,
          gameMode: data.gameMode
        } 
      });
      window.dispatchEvent(joinedEvent);
      
      // Request existing players from the server
      this.socket.emit('request-existing-players');
    });
    
    // Handle receiving existing players when joining a game that's already in progress
    this.socket.on('existing-players', (players) => {
      console.log('Received data about existing players:', players);
      
      // Create meshes for all existing players
      Object.keys(players).forEach(playerId => {
        // Skip creating a mesh for ourselves
        if (playerId === this.socket.id) return;
        
        const player = players[playerId];
        console.log(`Creating existing player ${playerId}:`, player);
        
        // Ensure we have a proper position object
        let playerPosition = null;
        if (player.position) {
          if (player.position instanceof THREE.Vector3) {
            playerPosition = player.position;
          } else {
            // Create a new Vector3 from the plain object
            playerPosition = new THREE.Vector3(
              player.position.x || 0,
              player.position.y || 0,
              player.position.z || 0
            );
          }
        }
        
        // In Free-For-All mode, each player gets a unique color
        this.addPlayer(
          playerId,
          playerPosition,
          null, // Let the addPlayer method assign a random color
          null, // No team in Free-For-All
          player.character
        );
      });
      
      // Dispatch an event with all existing players for GameView to update the player list
      const existingPlayersEvent = new CustomEvent('existing-players-received', {
        detail: { players }
      });
      window.dispatchEvent(existingPlayersEvent);
    });
    
    // Handle other players joining
    this.socket.on('player-joined', (data) => {
      console.log(`Player ${data.username} joined the game as ${data.character}`);
      
      // Create mesh for the new player with character type
      const playerColor = data.team === 'Red' ? 0xff0000 : 0x0000ff;
      
      // Position is null to use the default spawn point based on team
      this.addPlayer(
        data.id, 
        null, 
        playerColor, 
        data.team ? data.team.toLowerCase() : 'neutral',
        data.character
      );
      
      // Dispatch an event for GameView to update its player list
      // This ensures the local UI knows about all players
      const playerJoinedEvent = new CustomEvent('player-joined', { 
        detail: { 
          id: data.id,
          username: data.username,
          team: data.team,
          character: data.character
        } 
      });
      window.dispatchEvent(playerJoinedEvent);
      
      // Don't request player list here, let the GameView component handle refreshes
      // This avoids multiple clients requesting data simultaneously on player joined
    });
    
    // Handle player leaving
    this.socket.on('player-left', (data) => {
      console.log(`Player ${data.username} left the game`);
      
      // Remove the player using our helper method
      this.removePlayer(data.id);
    });
    
    // Handle position updates from other players
    this.socket.on('player-position', (data) => {
      // Update the position of the player with the given ID
      if (data.id !== this.socket.id) {
        // Get player from PlayerManager
        const playerMesh = this.playerManager.players[data.id];
        if (playerMesh) {
          // Always update the userData states first, so animation handling respects it
          if (playerMesh.userData) {
            // Update crouching state
            if (data.isCrouching !== undefined) {
              const previousCrouchState = playerMesh.userData.isCrouching;
              playerMesh.userData.isCrouching = data.isCrouching;
              
              // Debug log crouching state changes
              if (previousCrouchState !== data.isCrouching) {
                console.log(`Player ${data.id} crouching state changed: ${previousCrouchState} -> ${data.isCrouching}`);
              }
            }
            
            // Update jumping state
            if (data.isJumping !== undefined) {
              playerMesh.userData.isJumping = data.isJumping;
              
              // If jumping, also update the jump velocity for animations
              if (data.jumpVelocity !== undefined) {
                playerMesh.userData.jumpVelocity = data.jumpVelocity;
              }
            }
            
            // Update movement state - critical for animation sync
            if (data.isMoving !== undefined) {
              const previousMovingState = playerMesh.userData.isMoving;
              playerMesh.userData.isMoving = data.isMoving;
              
              // Log movement state changes
              if (previousMovingState !== data.isMoving) {
                console.log(`Player ${data.id} movement state changed: ${previousMovingState ? 'moving' : 'stopped'} -> ${data.isMoving ? 'moving' : 'stopped'}`);
              }
            }
          }
          
          // Handle immediate animation updates for movement state changes
          if (data.movementStateChanged === true && playerMesh.userData) {
            console.log(`Processing movement state change for player ${data.id}`);
            
            // Determine appropriate animation state based on current conditions
            const conditions = {
              isDead: playerMesh.userData.isDead || false,
              isJumping: playerMesh.userData.isJumping || false,
              jumpVelocity: playerMesh.userData.jumpVelocity || 0,
              isCrouching: playerMesh.userData.isCrouching || false,
              isMoving: data.isMoving,
              isRunning: false, // Default to regular movement speed
              moveDirection: 'forward' // Default to forward movement
            };
            
            // Force animation update immediately for movement state changes
            this.playerManager.animationController.forceAnimation(
              playerMesh, 
              data.isMoving ? (conditions.isCrouching ? 'crouchForward' : 'walk') : (conditions.isCrouching ? 'crouchIdle' : 'idle')
            );
          }
          
          // Ensure we have a proper Vector3 position object
          if (data.position) {
            const position = new THREE.Vector3(
              data.position.x || 0,
              data.position.y || 0,
              data.position.z || 0
            );
            
            // Update position using PlayerManager method which will now handle animations based on the states
            this.playerManager.updatePlayerPosition(playerMesh, position);
          }
          
          // Update rotation (only Y axis for now)
          if (data.rotation) {
            // Apply the rotation directly from the server data
            playerMesh.rotation.y = data.rotation.y;
          }
        } else {
          console.warn(`Received position update for non-existent player ${data.id}`);
        }
      }
    });
    
    // Handle shooting events from other players
    this.socket.on('player-shoot', (data) => {
      // Skip if it's our own shot (we already handled it locally)
      if (data.id === this.socket.id) return;
      
      // Get the player mesh from PlayerManager
      const playerMesh = this.playerManager.players[data.id];
      
      if (!playerMesh) {
        // If the player exists in the game but we don't have their mesh yet
        // This can happen if a player joined before us and we haven't received their full data
        if (data.position && data.direction) {
          // We can still create a shooting visual at the reported position
          if (data.weaponType === WEAPON_TYPES.KNIFE) {
            // For melee, we'd need the player mesh, so we'll skip this
            console.log(`Received melee attack from player ${data.id} but player mesh not found`);
          } else {
            // For guns, we can still show the gunshot visual using the position data
            const position = new THREE.Vector3(
              data.position.x, 
              data.position.y, 
              data.position.z
            );
            const direction = new THREE.Vector3(
              data.direction.x, 
              data.direction.y, 
              data.direction.z
            );
            this.playerManager.createGunShotVisualAtPosition(position, direction);
            
            // Play spatial gunshot sound
            if (this.weaponSystem) {
              this.weaponSystem.playSpatialSound(`${data.weaponType}_shoot`, position, {
                volume: 0.6, // Slightly lower volume for distant shots
                maxDistance: 100, // Maximum audible distance
                refDistance: 15  // Reference distance for volume drop-off
              });
            }
            
            console.log(`Created gunshot visual for player ${data.id} at position`, position);
          }
        }
        return;
      }
      
      // Create appropriate visual feedback based on weapon type
      if (data.weaponType === WEAPON_TYPES.KNIFE) {
        this.playerManager.createMeleeAttackVisual(playerMesh);
        
        // Play spatial knife swing sound
        if (this.weaponSystem) {
          const position = new THREE.Vector3().copy(playerMesh.position);
          this.weaponSystem.playSpatialSound(`${WEAPON_TYPES.KNIFE}_swing`, position, {
            volume: 0.5,
            maxDistance: 30, // Shorter distance for melee sounds
            refDistance: 10
          });
        }
        
        console.log(`Player ${data.id} performed melee attack`);
      } else {
        // Check if we have position and direction
        if (!data.position || !data.direction) {
          return;
        }
        
        const position = new THREE.Vector3(
          data.position.x, 
          data.position.y, 
          data.position.z
        );
        const direction = new THREE.Vector3(
          data.direction.x, 
          data.direction.y, 
          data.direction.z
        );
        this.playerManager.createGunShotVisual(playerMesh, position, direction);
        
        // Play spatial gunshot sound
        if (this.weaponSystem) {
          this.weaponSystem.playSpatialSound(`${data.weaponType}_shoot`, position, {
            volume: 0.6, // Slightly lower volume for distant shots
            maxDistance: 100, // Maximum audible distance
            refDistance: 15  // Reference distance for volume drop-off
          });
        }
        
        console.log(`Player ${data.id} fired weapon ${data.weaponType}`);
      }
      
      // If there's hit data, create hit effect
      if (data.hit && data.hit.position) {
        const hitPosition = new THREE.Vector3(
          data.hit.position.x, 
          data.hit.position.y, 
          data.hit.position.z
        );
        
        // Get the body part if provided, otherwise use default
        const bodyPart = data.hit.bodyPart || 'default';
        
        // Create hit effect with appropriate body part visual
        this.playerManager.createHitEffect(hitPosition, bodyPart);
        
        // Play spatial hit sound based on body part
        if (this.weaponSystem) {
          const hitSoundKey = bodyPart === 'head' ? 'hit_head' : 
                              (bodyPart === 'torso' ? 'hit_body' : 'hit_limb');
          
          this.weaponSystem.playSpatialSound(hitSoundKey, hitPosition, {
            volume: 0.4,
            maxDistance: 50,
            refDistance: 10
          });
        }
        
        // If this is a headshot, show a special effect
        if (bodyPart === 'head') {
          this.playerManager.createHeadshotEffect(hitPosition);
        }
        
        console.log(`Player ${data.id} hit at position`, hitPosition, `bodyPart: ${bodyPart}`);
      }
    });
    
    // Handle player health updates
    this.socket.on('player-health-update', (data) => {
      console.log(`Health update for player ${data.id}: ${data.health}/${data.maxHealth}`);
      
      // If this is the local player, update health
      if (data.id === this.socket.id) {
        this.playerHealth = data.health;
        this.maxHealth = data.maxHealth;
        this.isDead = data.isDead || false; // Use the isDead flag from server
        
        // Update weapon system with player death state
        if (this.weaponSystem) {
          this.weaponSystem.setPlayerDeathState(this.isDead);
        }
        
        // If player was eliminated, handle death state
        if (data.eliminated) {
          console.log('You were eliminated! Waiting for respawn...');
          this.isDead = true; // Set the isDead flag to prevent movement
          
          // Update weapon system with player death state
          if (this.weaponSystem) {
            this.weaponSystem.setPlayerDeathState(true);
          }
          
          // Play death animation for local player
          if (this.playerManager.localPlayer) {
            this.playerManager.handlePlayerDeath(this.playerManager.localPlayer);
            console.log('Playing death animation for local player');
          }
        }
      } else {
        // Update other player's health
        const player = this.playerManager.players[data.id];
        if (player) {
          // Store health in player userData
          if (!player.userData) player.userData = {};
          player.userData.health = data.health;
          player.userData.isDead = data.isDead || false;
          
          // If player was eliminated, play death animation
          if (data.eliminated) {
            console.log(`Player ${data.id} was eliminated!`);
            player.userData.isDead = true;
            
            // Use handlePlayerDeath instead of playPlayerAnimation for death
            this.playerManager.handlePlayerDeath(player);
            console.log(`Playing death animation for player ${data.id}`);
          }
        }
      }
      
      // Emit a custom event that the Vue component can listen for
      const healthUpdateEvent = new CustomEvent('player-health-update', { 
        detail: { 
          id: data.id,
          health: data.health,
          maxHealth: data.maxHealth,
          eliminated: data.eliminated,
          isLocalPlayer: data.id === this.socket.id
        } 
      });
      window.dispatchEvent(healthUpdateEvent);
    });
    
    // Handle player respawn
    this.socket.on('player-respawn', (data) => {
      console.log(`Player ${data.id} respawned with ${data.health} health at position:`, data.position);
      
      // If this is the local player
      if (data.id === this.socket.id) {
        this.playerHealth = data.health;
        this.isDead = false; // Reset the death flag
        
        // Reset death animation state
        this.isDeathAnimationPlaying = false;
        
        // Reset the death state in the weapon system
        if (this.weaponSystem) {
          this.weaponSystem.setPlayerDeathState(false);
        }
        
        // Always use the position provided by the server
        if (data.position) {
          // Create a Vector3 position
          const position = new THREE.Vector3(
            data.position.x || 0, 
            data.position.y || 0, 
            data.position.z || 0
          );
          
          // Update the local player's position in PlayerManager
          if (this.playerManager.localPlayer) {
            this.playerManager.localPlayer.position.copy(position);
            
            // Reset camera rotation back to normal
            this.camera.rotation.copy(this.cameraRotation);
            
            // Update camera position to proper height
            this.camera.position.set(
              position.x,
              position.y + PLAYER_EYE_HEIGHT,
              position.z
            );
            
            // Create respawn visual effect
            this.playerManager.createRespawnEffect(position);
            console.log(`Local player respawned at position:`, position);
            
            // Reset player state
            if (this.playerManager.localPlayer.userData) {
              this.playerManager.localPlayer.userData.isDead = false;
              
              // Force animation reset for local player respawn using the animation controller
              this.playerManager.animationController.forceAnimation(this.playerManager.localPlayer, 'idle');
            }
          }
        }
        
        // Set spawn protection status
        this.isSpawnProtected = data.spawnProtected === true;
        
        // Re-enable controls when respawning
        this.controlsEnabled = true;
      }
      // If this is another player
      else {
        const player = this.playerManager.players[data.id];
        
        if (player) {
          // Update player state
          if (!player.userData) player.userData = {};
          player.userData.health = data.health;
          player.userData.isDead = false;
          
          // Apply spawn protection effect if needed
          player.userData.isSpawnProtected = data.spawnProtected === true;
          if (player.userData.isSpawnProtected) {
            this.playerManager.addSpawnProtectionEffect(player, true);
          }
          
          // Update position if provided
          if (data.position) {
            const position = new THREE.Vector3(
              data.position.x || 0, 
              data.position.y || 0, 
              data.position.z || 0
            );
            
            // Update position using PlayerManager
            this.playerManager.updatePlayerPosition(player, position);
            
            // Force animation reset for remote player respawn
            this.playerManager.animationController.forceAnimation(player, 'idle');
            
            // Create respawn visual effect
            this.playerManager.createRespawnEffect(position);
            console.log(`Player ${data.id} respawned at position:`, position);
          }
        } else {
          console.log(`Cannot handle respawn for player ${data.id}: player mesh not found`);
        }
      }
      
      // Emit a custom event for respawn
      const respawnEvent = new CustomEvent('player-respawn', { 
        detail: { 
          id: data.id,
          health: data.health,
          position: data.position,
          isLocalPlayer: data.id === this.socket.id,
          spawnProtected: data.spawnProtected === true
        } 
      });
      window.dispatchEvent(respawnEvent);
    });
    
    // Handle disconnect
    this.socket.on('disconnect', () => {
      console.log('Disconnected from game server');
    });
    
    // Handle match start event
    this.socket.on('match-start', (data) => {
      console.log('Match started with duration:', data.durationSeconds);
      this.isGameStarted = true;
      
      // Create a custom event for the match start
      const matchStartEvent = new CustomEvent('match-start', {
        detail: data
      });
      window.dispatchEvent(matchStartEvent);
    });
    
    // Handle match end event
    this.socket.on('match-end', (data) => {
      console.log('Match ended. Winner:', data.winner);
      this.isGameStarted = false;
      
      // Unlock pointer at end of match
      if (this.mouse.locked) {
        this.unlockControls();
      }
      
      // Create a custom event for the match end
      const matchEndEvent = new CustomEvent('match-end', {
        detail: data
      });
      window.dispatchEvent(matchEndEvent);
    });
    
    this.socket.on('player-respawned', (data) => {
      // ... existing code for handling player respawn ...
      
      // Ensure the respawned player has idle animation
      const respawnedPlayer = this.players[data.id];
      if (respawnedPlayer) {
        this.playPlayerAnimation(respawnedPlayer, 'idle');
      }
    });
  }
  
  // Handle case when socket connection fails
  handleSocketConnectionFailure() {
    console.log('Continuing with local game experience without server connection');
    // Create a local player for single-player testing
    const team = Math.random() > 0.5 ? 'red' : 'blue';
    const playerColor = team === 'red' ? 0xff0000 : 0x0000ff;
    const character = Math.random() > 0.5 ? 'soldier' : 'scout';
    
    // Create local player with consistent ID
    this.localPlayer = this.addPlayer('local-player', null, playerColor, team, character);
    
    // Initialize weapon system after local player is created
    this.initWeaponSystem();
  }
  
  // Start animation loop
  start() {
    if (!this.isAnimating) {
      this.isAnimating = true;
      console.log('Starting animation loop');
      
      // If the animation frame isn't already active (like during a camera transition)
      // then restart the animation loop
      if (!this.animationFrame) {
        this.lastTimestamp = performance.now();
        this.animate(this.lastTimestamp);
      }
    }
  }
  
  // Stop animation loop
  stop() {
    this.isAnimating = false;
    
    // Only cancel animation frame if we're not in a camera transition
    // This prevents unnecessary stopping/starting during transitions
    if (!this.cameraTransitioning && this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      console.log('Stopped animation loop');
    }
  }
  
  // Animation loop - called repeatedly by requestAnimationFrame
  animate(timestamp) {
    // Calculate delta time for consistent movement speeds
    // Switch to shorter update interval when transitioning for smoother animation
    const deltaTime = this.cameraTransitioning 
      ? Math.min((timestamp - this.lastTimestamp) / 1000, 0.1) 
      : Math.min((timestamp - this.lastTimestamp) / 1000, 0.1);
    
    this.lastTimestamp = timestamp;
    
    // Exit if animation is stopped, but continue if camera is transitioning
    if (!this.isAnimating && !this.cameraTransitioning) {
      return;
    }
    
    // Set up the next animation frame
    this.animationFrame = requestAnimationFrame(this.animate.bind(this));
    
    try {
      // Update performance stats
      if (this.performanceSettings && this.performanceSettings.monitoring.enabled) {
        this.updatePerformanceStats();
      }
      
      // Update camera transition if active
      if (this.cameraTransitioning) {
        this.updateCameraTransition();
      }
      
      // Only update player movement if player has joined and game is running
      if (this.playerJoined && this.isAnimating) {
        // Update player movement with delta time
        this.updatePlayerMovement(deltaTime);
      }
      
      // Update weapon system if it exists and player has joined
      if (this.weaponSystem && this.playerJoined && this.isAnimating) {
        this.weaponSystem.update(this.camera);
      }
      
      // Update PlayerManager for animation mixers and effects
      if (this.playerManager) {
        this.playerManager.update(deltaTime);
      }
      
      // Apply frustum culling to improve performance (only if enabled)
      if (this.performanceSettings?.optimizations?.frustumCulling) {
        this.applyFrustumCulling();
      }
      
      // Improved render function with better error handling
      if (this.renderer && this.scene && this.camera) {
        // Use lower quality settings during initial scene load or transitions
        if (!this.playerJoined || this.cameraTransitioning) {
          this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.0));
        } else {
          // Use normal quality settings when gameplay is active
          const pixelRatio = this.performanceSettings.quality[this.performanceSettings.currentQuality].pixelRatio;
          this.renderer.setPixelRatio(pixelRatio);
        }
        
        try {
          // Regular render code follows...
          this.renderer.render(this.scene, this.camera);
        } catch (renderError) {
          // Add more specific diagnostic based on the error
          console.error('Error during rendering:', renderError);
        }
      } else {
        console.warn('Cannot render: renderer, scene, or camera is not available');
      }
    } catch (error) {
      console.error('Error in animation loop:', error);
      // Stop the animation if there's an error to prevent cascading errors
      this.stop();
    }
  }
  
  // Add frustum culling to improve performance by not rendering objects outside the camera view
  applyFrustumCulling() {
    // Skip if not initialized
    if (!this.camera || !this.scene) return;
    
    // Get the camera frustum in world space
    const frustum = new THREE.Frustum();
    const matrix = new THREE.Matrix4().multiplyMatrices(
      this.camera.projectionMatrix,
      this.camera.matrixWorldInverse
    );
    frustum.setFromProjectionMatrix(matrix);
    
    // Collect visible players based on quality settings
    if (this.playerManager && this.playerManager.players) {
      let visiblePlayerCount = 0;
      const maxVisiblePlayers = this.performanceSettings?.quality[this.performanceSettings.currentQuality]?.maxVisiblePlayers || 15;
      
      // Store distance to each player from camera
      const playerDistances = [];
      
      // Calculate distances to all players
      Object.values(this.playerManager.players).forEach(player => {
        if (!player.visible) return; // Skip already invisible players
        
        // Skip local player - always visible
        if (player.userData && player.userData.isLocalPlayer) return;
        
        // Get player position
        const position = player.position;
        
        // Calculate distance to camera
        const distance = this.camera.position.distanceTo(position);
        
        // Store with reference to player
        playerDistances.push({
          player,
          distance
        });
      });
      
      // Sort by distance
      playerDistances.sort((a, b) => a.distance - b.distance);
      
      // Apply visibility based on distance
      playerDistances.forEach((data, index) => {
        // Make closest players visible up to the max count
        data.player.visible = index < maxVisiblePlayers;
        
        // Update count if visible
        if (data.player.visible) {
          visiblePlayerCount++;
        }
      });
      
      if (playerDistances.length > maxVisiblePlayers) {
        console.log(`Culled ${playerDistances.length - maxVisiblePlayers} distant players for performance. Showing ${visiblePlayerCount} players.`);
      }
    }
    
    // MODIFIED: Only cull certain non-critical objects
    // Avoid culling terrain, grass, and other important scene elements
    this.scene.traverse(object => {
      // Skip processing for these categories - always keep visible
      if (
        // Skip objects explicitly marked to always be visible
        (object.userData && (
          object.userData.alwaysVisible ||
          object.userData.isMap ||
          object.userData.isTerrain ||
          object.userData.isUI
        )) ||
        // Skip objects with names indicating terrain, grass, etc.
        (object.name && (
          object.name.toLowerCase().includes('terrain') ||
          object.name.toLowerCase().includes('grass') ||
          object.name.toLowerCase().includes('ground') ||
          object.name.toLowerCase().includes('landscape') ||
          object.name.toLowerCase().includes('floor')
        )) ||
        // Skip entire scene groups that might contain landscape elements
        (object.userData && object.userData.type && (
          object.userData.type.toLowerCase().includes('terrain') ||
          object.userData.type.toLowerCase().includes('landscape') ||
          object.userData.type.toLowerCase().includes('environment')
        )) ||
        // ADDED: Skip weapon hands/models from the WeaponSystem
        (object.userData && (
          object.userData.isWeaponHands || 
          object.userData.attachedWeapon
        )) ||
        // Skip if object has frustumCulled explicitly set to false
        (object.frustumCulled === false) ||
        // Skip objects with names indicating weapon parts
        (object.name && (
          object.name.toLowerCase().includes('hand') ||
          object.name.toLowerCase().includes('weapon') ||
          object.name.toLowerCase().includes('gun') ||
          object.name.toLowerCase().includes('rifle') ||
          object.name.toLowerCase().includes('pistol') ||
          object.name.toLowerCase().includes('knife')
        ))
      ) {
        // Always keep these visible
        return;
      }
      
      // Skip player objects (already handled above)
      if (object.userData && object.userData.isPlayer) {
        return;
      }
      
      // Skip non-mesh objects
      if (!object.isMesh || !object.geometry) {
        return;
      }
      
      // Compute bounding sphere if not available
      if (!object.geometry.boundingSphere) {
        object.geometry.computeBoundingSphere();
      }
      
      // Get world position of the center of the bounding sphere
      const center = object.geometry.boundingSphere.center.clone();
      const radius = object.geometry.boundingSphere.radius;
      
      // Transform to world coordinates
      object.updateMatrixWorld();
      center.applyMatrix4(object.matrixWorld);
      
      // Add a small buffer to the radius to prevent objects at frustum edge from popping
      const bufferRadiusFactor = 1.2; // 20% larger radius for testing
      
      // Test if the object's bounding sphere is in the frustum with the buffer
      const inFrustum = frustum.intersectsSphere(
        new THREE.Sphere(center, radius * object.scale.x * bufferRadiusFactor)
      );
      
      // Update visibility only for non-critical objects
      object.visible = inFrustum;
    });
  }
  
  // Helper method to properly remove a player from the scene
  removePlayer(playerId) {
    if (this.playerManager) {
      this.playerManager.removePlayer(playerId);
      
      // If this was the local player, also clear reference
      if (playerId === this.socket?.id && this.localPlayer) {
        this.localPlayer = null;
      } else {
        // Remove from players object - now handled by PlayerManager
        // We only need to delete references we maintain
        delete this.players[playerId];
      }
    }
  }
  
  // Method to add player representation to the scene with character type
  addPlayer(id, position, color, team, character = 'soldier', forcedSpawn = false) {
    // Use the PlayerManager to create and manage the player
    const playerMesh = this.playerManager.createPlayer({
      id,
      position,
      color,
      team,
      character,
      isLocalPlayer: false,
      forcedSpawn
    });
    
    // Store reference in our local players object for compatibility with existing code
    this.players[id] = playerMesh;
    
    console.log(`Added player ${id} to scene with character ${character}`);
    return playerMesh;
  }
  
  // Create player after successfully joining the game
  createPlayer(data) {
    try {
      //  console.log('Creating player with data:', data);
      
      // Store health information
      this.playerHealth = data.health;
      this.maxHealth = data.health; // Store max health for reference
      
      // Get player ID from socket ID (for local player) or from data (for other players)
      let playerId;
      if (data.id) {
        playerId = data.id;
      } else if (this.socket && this.socket.id) {
        playerId = this.socket.id;
      } else {
        // Temporary ID if socket is not connected yet
        playerId = 'local-player-' + Date.now();
        console.warn('Socket not connected yet, using temporary player ID');
      }
      
      //console.log(`Creating local player with ID: ${playerId}`);
      
      // Create player through PlayerManager
      // In Free-For-All mode, team might be undefined, so add a default
      const team = data.team || 'Neutral';
      const playerColor = team === 'Red' ? 0xff0000 : (team === 'Blue' ? 0x0000ff : 0x888888);
      
      // Convert to lowercase safely with null check
      const teamLowerCase = team.toLowerCase();
      
      // Create the local player via PlayerManager
      this.localPlayer = this.playerManager.createPlayer({
        id: playerId,
        position: data.position,
        color: playerColor,
        team: teamLowerCase,
        character: data.character,
        isLocalPlayer: true,
        forcedSpawn: data.forcedSpawn
      });
      
      console.log(`Created player mesh with team ${team} and character ${data.character} at position:`, data.position || 'default position');
      
      // If we already have a weapon system, update the local player ID
      if (this.weaponSystem) {
        this.weaponSystem.localPlayerId = playerId;
      }
      
      // Create a custom event for other components to listen for
      const playerCreatedEvent = new CustomEvent('player-created', {
        detail: { 
          playerId: playerId,
          team: team,
          character: data.character,
          health: data.health,
          position: data.position
        } 
      });
      
      window.dispatchEvent(playerCreatedEvent);
      
      return this.localPlayer;
    } catch (error) {
      console.error('Error creating player:', error);
      return null;
    }
  }
  
  // Method to update player position
  updatePlayerPosition(playerGroup, position) {
    this.playerManager.updatePlayerPosition(playerGroup, position);
  }
  
  // Method to update player animation based on movement state
  updatePlayerAnimation() {
    if (!this.localPlayer || !this.localPlayer.userData || !this.playerManager) return;
    
    // Skip animation update if player is dead or death animation is playing
    if (this.isDead || this.localPlayer.userData.currentAnimation === 'death') {
      return;
    }
    
    // Determine player state conditions
    const isMoving = this.keys.forward || this.keys.backward || this.keys.left || this.keys.right;
    const isRunning = this.keys.run && isMoving;
    
    // Determine movement direction based on key presses
    let moveDirection = 'forward';
    if (this.keys.backward) moveDirection = 'backward';
    else if (this.keys.left) moveDirection = 'left';
    else if (this.keys.right) moveDirection = 'right';
    
    // Create a conditions object for the animation controller
    const conditions = {
      isDead: this.isDead,
      isJumping: this.isJumping,
      jumpVelocity: this.jumpVelocity,
      isCrouching: this.isCrouching,
      isMoving,
      isRunning,
      moveDirection
    };
    
    // Update the local player's userData with current state
    this.localPlayer.userData.isCrouching = this.isCrouching;
    this.localPlayer.userData.isJumping = this.isJumping;
    this.localPlayer.userData.jumpVelocity = this.jumpVelocity;
    
    // Use the PlayerManager's animation controller to update animation
    this.playerManager.animationController.updateAnimation(this.localPlayer, conditions);
    
    // Additional local tracking for sync with server
    this.lastAnimationUpdate = {
      time: Date.now(),
      conditions
    };
  }
  
  // Modify the playPlayerAnimation method to sync with WeaponSystem's hands
  playPlayerAnimation(playerGroup, animationName) {
    // First, call the PlayerManager's animation method 
    const animationPlayed = this.playerManager.playPlayerAnimation(playerGroup, animationName);
    
    // If this is the local player and we have a WeaponSystem, synchronize hands animation
    if (playerGroup.userData && playerGroup.userData.isLocalPlayer && this.weaponSystem) {
      // Call WeaponSystem's playHandsAnimation method to update the hands animation
      this.weaponSystem.playHandsAnimation(animationName);
      console.log(`Syncing hands animation in WeaponSystem to: ${animationName}`);
    }
    
    return animationPlayed;
  }
  
  // Clean up resources when the scene is no longer needed
  dispose() {
    console.log('Disposing MainScene resources');
    
    // Stop the animation loop
    this.stop();
    
    // Clean up socket connection
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    // Remove event listeners
    window.removeEventListener('resize', this.handleResize);
    
    // Remove control event listeners
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('click', this.onMouseClick);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    
    // Clean up performance monitoring
    if (this.fpsDisplay && this.fpsDisplay.parentNode) {
      this.fpsDisplay.parentNode.removeChild(this.fpsDisplay);
      this.fpsDisplay = null;
    }
    
    // Unlock controls if they're locked
    if (this.mouse.locked) {
      this.unlockControls();
    }
    
    // Stop automatic fire if it's running
    this.stopAutoFire();
    
    // Dispose of skybox if it exists
    if (this.skybox) {
      this.skybox.dispose();
      this.skybox = null;
    }
    
    // Clean up weapon system
    if (this.weaponSystem) {
      this.weaponSystem.dispose();
      this.weaponSystem = null;
    }
    
    // Clean up player manager
    if (this.playerManager) {
      this.playerManager.dispose();
      this.playerManager = null;
    }
    
    // Disconnect Socket.IO
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    // Clean up map resources
    if (this.mapScene) {
      this.mapScene.dispose();
      this.mapScene = null;
    }
    
    // Clean up the scene
    this.scene.traverse(object => {
      if (object.geometry) {
        object.geometry.dispose();
      }
      
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach(material => material.dispose());
        } else {
          object.material.dispose();
        }
      }
    });
    
    // Clear the scene
    while(this.scene.children.length > 0) { 
      this.scene.remove(this.scene.children[0]); 
    }
    
    // Other cleanup as needed
    if (this.renderer) {
      this.renderer.dispose();
      
      // Remove the canvas from the DOM
      if (this.container && this.renderer.domElement) {
        this.container.removeChild(this.renderer.domElement);
      }
    }
    
    // Clear pool stats interval
    if (this.poolStatsInterval) {
      clearInterval(this.poolStatsInterval);
      this.poolStatsInterval = null;
    }
  }
  
  // Method to set player data and join game
  setPlayerData(playerData) {
    // Store player data
    this.playerData = playerData;
    
    // Set character-specific attributes
    if (playerData.character === 'scout') {
      this.characterSpeed = 6; // Reduced from 9 to 4.5 (50% reduction)
      this.maxHealth = 80;     // Scout has less health
    } else {
      this.characterSpeed = 6; // Reduced from 6 to 3 (50% reduction)
      this.maxHealth = 100;    // Soldier has standard health
    }
    
    // Initialize health to max health
    this.playerHealth = this.maxHealth;
    
    // Set team
    this.team = playerData.team;
    
    console.log(`Player data set: ${playerData.username}, Team: ${this.team}, Character: ${playerData.character}`);
    console.log(`Character speed: ${this.characterSpeed}, Max health: ${this.maxHealth}`);
    
    // If connected to the server, join the game
    if (this.socket && this.socket.connected) {
      // If server assigned a team, use that instead of the selected team
      if (this.assignedTeam) {
        playerData.team = this.assignedTeam;
      }
      
      console.log('Emitting join-game event with player data:', playerData);
      this.socket.emit('join-game', playerData);
    }
  }
  
  // Setup keyboard and mouse controls
  setupControls() {
    // Set up keyboard event listeners
    document.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('keyup', this.onKeyUp.bind(this));
    
    // Set up mouse event listeners for camera rotation
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    
    // Set up mouse events for shooting
    document.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
    // Keep click for single shots and pointer lock
    document.addEventListener('click', this.onMouseClick.bind(this));
    
    // Request pointer lock on canvas click
    this.renderer.domElement.addEventListener('click', () => {
      // Only lock controls if player has joined
      if (!this.mouse.locked && this.playerJoined) {
        this.lockControls();
      }
    });
    
    // Handle pointer lock change
    document.addEventListener('pointerlockchange', this.onPointerLockChange.bind(this));
    document.addEventListener('pointerlockerror', () => {
      console.error('Pointer lock error');
    });

    // Add emergency respawn key and debug controls
    console.log("Press 'R' to reload weapon, 'K' to respawn if stuck, or 'H' to toggle hitbox visualization");
  }
  
  // Enable mouse controls after player has joined
  enableMouseControls() {
    if (this.renderer && this.renderer.domElement) {
      this.lockControls();
    }
  }
  
  // Lock/unlock pointer controls
  lockControls() {
    try {
      if (this.renderer && this.renderer.domElement) {
        this.renderer.domElement.requestPointerLock();
      } else {
        console.error('Cannot lock pointer: renderer or domElement is not available');
      }
    } catch (error) {
      console.error('Pointer lock error', error);
    }
  }
  
  unlockControls() {
    document.exitPointerLock();
  }
  
  // Handle pointer lock change event
  onPointerLockChange() {
    this.mouse.locked = document.pointerLockElement === this.renderer.domElement;
  }
  
  // Handle keyboard input
  onKeyDown(event) {
    // If player hasn't joined yet, ignore keyboard input for game controls
    if (!this.playerJoined) return;
    
    // Prevent default actions for game control keys
    if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space', 'ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'KeyR', 'KeyC', 'KeyT', 'Digit1', 'Digit2', 'Digit3', 'KeyK', 'KeyH', 'KeyJ', 'KeyB', 'KeyP', 'Tab', 'Escape', 'F1', 'KeyA'].includes(event.code)) {
      event.preventDefault();
    }
    
    // If controls are locked or chat is open, don't handle movement keys
    if (!this.mouse.locked || this.chatOpen) return;
    
    // Debug logging for all key presses
    //console.log(`Key pressed: ${event.code}`);
    
    // Handle movement keys
    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.keys.forward = true;
        // Update animation if needed based on crouch state
        this.updatePlayerAnimation();
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.keys.backward = true;
        // Update animation if needed based on crouch state
        this.updatePlayerAnimation();
        break;
      case 'KeyA':
        if (event.shiftKey && event.ctrlKey) {
          // Use Ctrl+Shift+A for animation debugging
          this.toggleAnimationDebug();
        } else {
          // Regular A key behavior (move left)
          this.keys.left = true;
          this.updatePlayerAnimation();
        }
        break;
      case 'ArrowLeft':
        this.keys.left = true;
        this.updatePlayerAnimation();
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.keys.right = true;
        // Update animation if needed based on crouch state
        this.updatePlayerAnimation();
        break;
      case 'Space':
        // Jump when space is pressed, but only if on the ground and space has been released since last jump
        if (this.onGround && !this.isCrouching && this.canJump) {
          this.keys.jump = true;
          this.isJumping = true;
          this.onGround = false;
          this.jumpVelocity = this.jumpStrength;
          this.canJump = false; // Prevent jumping again until space is released
        }
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.keys.run = true;
        break;
      // Handle C key for HOLD-TO-CROUCH (not toggle)
      case 'KeyC':
        console.log("KeyC DOWN - Enabling crouch");
        
        // Enable crouching while key is pressed
        this.isCrouching = true;
        console.log(`Crouch state set to: ${this.isCrouching}`);
        
        // Make sure PLAYER_EYE_HEIGHT is defined
        if (!this.PLAYER_EYE_HEIGHT) {
          this.PLAYER_EYE_HEIGHT = 1.6; // Default if not defined
        }
        
        // Set camera height target for crouching
        this.cameraHeightTarget = this.PLAYER_EYE_HEIGHT * 0.6; // 60% height when crouched
        console.log(`Set cameraHeightTarget to ${this.cameraHeightTarget}`);
        
        // Initialize cameraHeightCurrent if it's not set
        if (this.cameraHeightCurrent === undefined) {
          this.cameraHeightCurrent = this.PLAYER_EYE_HEIGHT;
        }
        
        // Record the time for smooth transition
        this.lastCrouchToggleTime = performance.now() / 1000;
        
        // Update player animation based on current movement state
        this.updatePlayerAnimation();
        
        // Set the local player's userData crouching state if available
        if (this.localPlayer && this.localPlayer.userData) {
          this.localPlayer.userData.isCrouching = true;
        }
        
        // Force immediate network sync
        this.sendPositionUpdate(true);
        break;
      // Other keys remain the same
      case 'KeyR':
        if (this.weaponSystem) {
          this.weaponSystem.reload();
        }
        break;
      case 'KeyK':
        this.respawnPlayer();
        break;
      case 'KeyH':
        this.showHitboxes = !this.showHitboxes;
        if (this.showHitboxes) {
          this.updateHitboxVisualizations();
        } else {
          this.clearHitboxVisualizations();
        }
        break;
      case 'KeyT':
        // Handle target cache settings
        if (this.weaponSystem) {
          // Shift+T increases cache interval, Ctrl+T decreases it
          if (event.shiftKey) {
            const currentInterval = this.weaponSystem.targetCache.updateInterval;
            const newInterval = currentInterval + 100; // Increase by 100ms
            this.weaponSystem.setTargetCacheUpdateInterval(newInterval);
            console.log(`Increased target cache update interval to ${newInterval}ms`);
          } else if (event.ctrlKey) {
            const currentInterval = this.weaponSystem.targetCache.updateInterval;
            const newInterval = Math.max(100, currentInterval - 100); // Decrease by 100ms but min 100ms
            this.weaponSystem.setTargetCacheUpdateInterval(newInterval);
            console.log(`Decreased target cache update interval to ${newInterval}ms`);
          } else {
            // Toggle target caching
            const isEnabled = this.weaponSystem.toggleTargetCaching();
            console.log(`Target caching is now ${isEnabled ? 'enabled' : 'disabled'}`);
          }
        }
        break;
      case 'Digit1':
      case 'Digit2':
      case 'Digit3':
        if (this.weaponSystem) {
          const keyNumber = parseInt(event.code.replace('Digit', ''));
          this.weaponSystem.switchWeaponByKey(keyNumber);
        }
        break;
      case 'KeyP':
        // Display hit detection statistics when P is pressed
        if (this.weaponSystem) {
          const stats = this.weaponSystem.getHitStatsReport();
          console.log('%c=== HIT DETECTION STATISTICS ===', 'font-weight: bold; color: #44f');
          console.log('%cShots:', 'font-weight: bold');
          console.log(`  Total: ${stats.shots.total}`);
          console.log(`  Hits: ${stats.shots.hits}`);
          console.log(`  Misses: ${stats.shots.misses}`);
          console.log(`  Accuracy: ${stats.shots.accuracy}`);
          
          console.log('%cHits by Body Part:', 'font-weight: bold');
          console.log(`  Head: ${stats.bodyParts.head}`);
          console.log(`  Torso: ${stats.bodyParts.torso}`);
          console.log(`  Limbs: ${stats.bodyParts.limb}`);
          console.log(`  Hands/Feet: ${stats.bodyParts.hand}`);
          console.log(`  Other: ${stats.bodyParts.other}`);
          
          console.log('%cHits by Weapon:', 'font-weight: bold');
          console.log(`  Knife: ${stats.weapons.knife}`);
          console.log(`  Pistol: ${stats.weapons.pistol}`);
          console.log(`  Machine Gun: ${stats.weapons.machineGun}`);
          
          console.log('%cPerformance (Averages):', 'font-weight: bold');
          console.log(`  Total Time: ${stats.timing.averageTotal}`);
          console.log(`  Scene Traversal: ${stats.timing.averageTraverse}`);
          console.log(`  Intersection Test: ${stats.timing.averageIntersect}`);
          console.log(`  Hit Processing: ${stats.timing.averageProcess}`);
          
          console.log('%cScene Complexity:', 'font-weight: bold');
          console.log(`  Total Objects: ${stats.scene.totalObjects}`);
          console.log(`  Potential Targets: ${stats.scene.potentialTargets}`);
          
          console.log('%cTarget Cache:', 'font-weight: bold');
          console.log(`  Enabled: ${stats.targetCache.enabled}`);
          console.log(`  Update Interval: ${stats.targetCache.updateInterval}`);
          console.log(`  Cached Targets: ${stats.targetCache.cachedTargets}`);
          console.log(`  Time Since Update: ${stats.targetCache.timeSinceUpdate}`);
          console.log(`  Scene Version: ${stats.targetCache.sceneVersion}`);
          
          console.log('%c================================', 'font-weight: bold; color: #44f');
          
          // Print optimization suggestions based on timing
          this.printHitDetectionOptimizationSuggestions(stats);
        } else {
          console.log('Weapon system not initialized yet');
        }
        break;
      case 'F1':
        // Display help for profiling shortcuts
        this.displayProfilingHelp();
        break;
    }
  }
  
  // New method to analyze hit detection stats and suggest optimizations
  printHitDetectionOptimizationSuggestions(stats) {
    console.log('%c=== OPTIMIZATION SUGGESTIONS ===', 'font-weight: bold; color: #4a4');
    
    // Analyze the distribution of time
    const traversePercent = (parseFloat(stats.timing.averageTraverse) / parseFloat(stats.timing.averageTotal) * 100).toFixed(1);
    const intersectPercent = (parseFloat(stats.timing.averageIntersect) / parseFloat(stats.timing.averageTotal) * 100).toFixed(1);
    const processPercent = (parseFloat(stats.timing.averageProcess) / parseFloat(stats.timing.averageTotal) * 100).toFixed(1);
    
    console.log(`Time distribution: Traverse=${traversePercent}%, Intersect=${intersectPercent}%, Process=${processPercent}%`);
    
    // Check scene complexity
    if (stats.scene.totalObjects > 1000) {
      console.log('- High scene complexity detected. Consider:');
      console.log('  * Using a spatial partitioning system (e.g., Octree)');
      console.log('  * Implementing frustum culling for hit detection');
      console.log('  * Reducing number of objects through merging static geometry');
    }
    
    // Check for traversal bottlenecks
    if (parseFloat(traversePercent) > 40) {
      console.log('- Scene traversal is taking significant time. Consider:');
      console.log('  * Maintaining a separate list of shootable objects');
      console.log('  * Adding a "shootable" flag to objects for faster filtering');
      console.log('  * Using object pooling to reduce garbage collection during traversal');
      
      // Add target cache recommendation if it's disabled
      if (!stats.targetCache.enabled) {
        console.log('  * Enable target caching to reduce scene traversal cost (press T)');
      } else if (parseFloat(stats.targetCache.updateInterval) < 300) {
        console.log('  * Increase target cache update interval to reduce traversal frequency (press Shift+T)');
      }
    }
    
    // Check for intersection bottlenecks
    if (parseFloat(intersectPercent) > 40) {
      console.log('- Raycast intersection testing is taking significant time. Consider:');
      console.log('  * Using simpler collision geometries for hit detection');
      console.log('  * Implementing a two-phase detection (broad and narrow phase)');
      console.log('  * Adding detail level switching for collision meshes based on distance');
    }
    
    // Check for processing bottlenecks
    if (parseFloat(processPercent) > 40) {
      console.log('- Hit processing is taking significant time. Consider:');
      console.log('  * Simplifying body part detection logic');
      console.log('  * Pre-calculating and storing body part data on meshes');
      console.log('  * Reducing parent hierarchy traversal by storing direct references');
    }
    
    // Check accuracy rate
    const accuracyValue = parseFloat(stats.shots.accuracy);
    if (!isNaN(accuracyValue) && accuracyValue < 20 && stats.shots.total > 10) {
      console.log('- Low hit accuracy detected. This could indicate:');
      console.log('  * Collision meshes don\'t match visual meshes well');
      console.log('  * Ray casting direction may need adjustment');
      console.log('  * Network synchronization issues for multiplayer');
    }
    
    // Add target cache advice based on specific conditions
    if (stats.targetCache.enabled) {
      // Calculate time since last cache update
      const timeSinceUpdate = parseInt(stats.targetCache.timeSinceUpdate);
      const updateInterval = parseInt(stats.targetCache.updateInterval);
      
      // If cache is close to stale, recommend a smaller update interval
      if (timeSinceUpdate > updateInterval * 0.8) {
        console.log('- Target cache is becoming stale. Consider:');
        console.log('  * Decreasing the update interval (press Ctrl+T)');
        console.log('  * Forcing a cache update on important scene changes');
      }
      
      // If total time is still high with caching enabled
      if (parseFloat(stats.timing.averageTotal) > 10) {
        console.log('- Performance is still sub-optimal with target caching. Consider:');
        console.log('  * Implementing a spatial index (Octree/BVH) for faster intersection tests');
        console.log('  * Optimizing object hierarchies to reduce traversal depth');
      }
    } else {
      if (stats.scene.totalObjects > 100) {
        console.log('- Target caching is disabled. Enable it with T key to reduce traversal costs');
      }
    }
    
    console.log('%c================================', 'font-weight: bold; color: #4a4');
  }
  
  onKeyUp(event) {
    // If player hasn't joined yet, ignore keyboard input
    if (!this.playerJoined) return;
    
    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.keys.forward = false;
        // Update animation if needed based on crouch state
        this.updatePlayerAnimation();
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.keys.backward = false;
        // Update animation if needed based on crouch state
        this.updatePlayerAnimation();
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.keys.left = false;
        // Update animation if needed based on crouch state
        this.updatePlayerAnimation();
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.keys.right = false;
        // Update animation if needed based on crouch state
        this.updatePlayerAnimation();
        break;
      case 'Space':
        this.keys.jump = false;
        this.canJump = true; // Allow jumping again after space is released
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.keys.run = false;
        break;
      case 'KeyC':
        console.log(`KeyC UP - Disabling crouch`);
        
        // Disable crouching
        this.isCrouching = false;
        console.log(`Crouch state set to: ${this.isCrouching}`);
        
        // Make sure PLAYER_EYE_HEIGHT is defined
        if (!this.PLAYER_EYE_HEIGHT) {
          this.PLAYER_EYE_HEIGHT = 1.6; // Default if not defined
        }
        
        // Reset camera height target
        this.cameraHeightTarget = this.PLAYER_EYE_HEIGHT;
        console.log(`Set cameraHeightTarget to ${this.cameraHeightTarget}`);
        
        // Update local player's userData if available
        if (this.localPlayer && this.localPlayer.userData) {
          this.localPlayer.userData.isCrouching = false;
        }
        
        // Update player animation for standing up if playerManager and localPlayer exist
        this.updatePlayerAnimation();
        
        // Force position update to broadcast new crouch state
        this.sendPositionUpdate(true);
        break;
    }
  }
  
  // Handle mouse movement for camera rotation
  onMouseMove(event) {
    // If player hasn't joined yet, ignore mouse movement
    if (!this.playerJoined) return;
    
    if (this.mouse.locked) {
      // Update camera rotation based on mouse movement
      // Calculate mouse movement with sensitivity
      const sensitivity = 0.002;
      const movementX = event.movementX || 0;
      const movementY = event.movementY || 0;
      
      // Rotate camera based on mouse movement
      this.cameraRotation.y -= movementX * sensitivity;
      this.cameraRotation.x -= movementY * sensitivity;
      
      // Limit vertical rotation to prevent camera flipping
      this.cameraRotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.cameraRotation.x));
      
      // Apply rotation to camera
      this.camera.rotation.copy(this.cameraRotation);
      
      // Also rotate the local player model to match camera's Y rotation (horizontal only)
      if (this.localPlayer) {
        // For the player model, we only rotate around the Y axis (horizontal rotation)
        // We add PI (180 degrees) because the model is initially rotated to face the camera
        this.localPlayer.rotation.y = this.cameraRotation.y;
      }
    }
  }
  
  // Handle mouse button press for starting shooting
  onMouseDown(event) {
    // If player hasn't joined yet, ignore mouse input
    if (!this.playerJoined) return;
    
    // Only handle primary button (left click) when pointer is locked
    if (event.button === 0 && this.mouse.locked && this.weaponSystem) {
      // Clear spawn protection when shooting
      if (this.isSpawnProtected) {
        console.log('Spawn protection removed due to shooting');
        this.isSpawnProtected = false;
        this.updateSpawnProtectionVisual(false);
        
        // Clear the protection timer
        if (this.spawnProtectionTimer) {
          clearTimeout(this.spawnProtectionTimer);
          this.spawnProtectionTimer = null;
        }
      }
      
      // For machine gun, start automatic fire
      if (this.weaponSystem.currentWeapon === WEAPON_TYPES.MACHINE_GUN) {
        this.startAutoFire();
      } else {
        // For other weapons, just shoot once
        this.weaponSystem.shoot();
      }
    }
  }
  
  // Handle mouse click for single shots and pointer lock
  onMouseClick(event) {
    // Don't request pointer lock at all before joining the game
    if (!this.playerJoined) {
      return;
    }
    
    // Only lock the pointer if the game has started or we're in test mode without a server
    if (!this.socket) {
      // For local testing without a server, allow pointer lock
      this.lockControls();
    } else if (!this.mouse.locked) {
      // Allow pointer lock for movement
      this.lockControls();
    }
  }
  
  // Handle mouse button release
  onMouseUp(event) {
    // If player hasn't joined yet, ignore mouse input
    if (!this.playerJoined) return;
    
    // Stop automatic fire when left mouse button is released
    if (event.button === 0) {
      this.stopAutoFire();
    }
  }
  
  // Start automatic fire for machine gun
  startAutoFire() {
    // Clear any existing interval first
    this.stopAutoFire();
    
    // Only start autofire for machine gun
    if (this.weaponSystem && this.weaponSystem.currentWeapon === WEAPON_TYPES.MACHINE_GUN) {
      console.log('Starting automatic fire');
      
      // First shot immediately
      this.weaponSystem.shoot();
      
      // Get the actual fire rate from the weapon config
      const fireRate = this.weaponSystem.getFireRate();
      console.log(`Machine gun fire rate: ${fireRate} shots/second`);
      
      // Then continue at the weapon's fire rate
      this.autoFireInterval = setInterval(() => {
        if (this.weaponSystem && 
            this.weaponSystem.currentWeapon === WEAPON_TYPES.MACHINE_GUN && 
            this.weaponSystem.canShoot()) {
          this.weaponSystem.shoot();
        } else if (!this.weaponSystem || this.weaponSystem.currentWeapon !== WEAPON_TYPES.MACHINE_GUN) {
          // Stop if weapon changed
          this.stopAutoFire();
        }
      }, 1000 / fireRate); // Convert fire rate to milliseconds
    }
  }
  
  // Stop automatic fire
  stopAutoFire() {
    if (this.autoFireInterval) {
      clearInterval(this.autoFireInterval);
      this.autoFireInterval = null;
    }
  }
  
  // Update player movement based on key input
  updatePlayerMovement(deltaTime) {
    // Add a guard clause to check if player exists
    if (!this.localPlayer || !this.localPlayer.userData) {
      // Player not initialized yet, skip movement update
      return;
    }
    
    // Handle camera animation when player is dead
    if (this.playerHealth <= 0 || this.isDead) {
      // If we just died, start the death camera animation
      if (!this.isDeathAnimationPlaying) {
        this.isDeathAnimationPlaying = true;
        this.deathAnimationStartTime = Date.now();
        this.cameraOriginalHeight = this.camera.position.y - this.localPlayer.position.y;
        this.cameraOriginalPosition = this.camera.position.clone();
        
        // Tell the weapon system to play death animation for hands
        if (this.weaponSystem) {
          this.weaponSystem.setPlayerDeathState(true);
        }
      }
      
      // Animate camera falling to the ground
      const deathAnimDuration = 1500; // 1.5 seconds
      const elapsed = Date.now() - this.deathAnimationStartTime;
      const progress = Math.min(1, elapsed / deathAnimDuration);
      
      if (progress < 1) {
        // Calculate ground level at player position
        const groundLevel = this.getGroundLevel(this.localPlayer.position.x, this.localPlayer.position.z);
        
        // Smoothly lower camera to ground level
        const targetY = groundLevel + 0.1; // Slightly above ground to avoid clipping
        const currentY = this.cameraOriginalPosition.y;
        
        // Use easeOutQuad for more natural falling motion
        const easeOutProgress = 1 - Math.pow(1 - progress, 2);
        this.camera.position.y = currentY - (currentY - targetY) * easeOutProgress;
        
        // Add a slight forward tilt as if falling face forward
        const maxTilt = 0.8; // About 45 degrees
        this.camera.rotation.x = this.cameraRotation.x + maxTilt * easeOutProgress;
      }
      
      return; // Skip regular movement
    } else if (this.isDeathAnimationPlaying) {
      // Reset camera when player respawns
      this.isDeathAnimationPlaying = false;
      
      // Reset camera rotation
      this.camera.rotation.copy(this.cameraRotation);
      
      // Tell weapon system to reset
      if (this.weaponSystem) {
        this.weaponSystem.setPlayerDeathState(false);
      }
    }

    // Calculate movement speed based on character and whether running
    // Multiply by deltaTime and a normalization factor to maintain consistent speed at 60 FPS
    const normalizedDeltaTime = deltaTime * 60; // Normalize to 60 FPS
    const currentSpeed = this.moveSpeed * this.characterSpeed * (this.keys.run ? this.runMultiplier : 1) * normalizedDeltaTime;
    
    // Reset velocity
    this.playerVelocity.set(0, 0, 0);
    this.playerDirection.set(0, 0, 0);
    
    // Set direction based on camera rotation
    if (this.keys.forward) {
      this.playerDirection.z = -1;
    }
    if (this.keys.backward) {
      this.playerDirection.z = 1;
    }
    if (this.keys.left) {
      this.playerDirection.x = -1;
    }
    if (this.keys.right) {
      this.playerDirection.x = 1;
    }
    
    // Normalize direction if moving diagonally
    if (this.playerDirection.length() > 0) {
      this.playerDirection.normalize();
    }
    
    // Calculate velocity based on direction and speed
    this.playerVelocity.x = this.playerDirection.x * currentSpeed;
    this.playerVelocity.z = this.playerDirection.z * currentSpeed;
    
    // Apply jumping and gravity
    if (this.isJumping) {
      // Using direct velocity without applying delta time here, as we already adjusted jumpVelocity
      this.playerVelocity.y = this.jumpVelocity;
      
      // Apply gravity to reduce jump velocity, scaled by deltaTime
      this.jumpVelocity -= this.gravity * normalizedDeltaTime;
      
      // If we've reached the ground or gone below it
      const groundLevel = this.getGroundLevel(this.localPlayer.position.x, this.localPlayer.position.z);
      if (this.localPlayer.position.y + this.playerVelocity.y <= groundLevel) {
        // Reset jump state and snap to ground
        this.isJumping = false;
        this.onGround = true;
        this.jumpVelocity = 0;
        this.playerVelocity.y = groundLevel - this.localPlayer.position.y;
        
        // If space is not pressed, ensure canJump is true for next jump
        if (!this.keys.jump) {
          this.canJump = true;
        }
      }
    }
    
    // Create rotation matrix from camera rotation (y axis only)
    const rotationMatrix = new THREE.Matrix4();
    rotationMatrix.makeRotationY(this.cameraRotation.y);
    
    // Apply rotation to velocity (only for x and z components)
    const rotatedVelocity = new THREE.Vector3(this.playerVelocity.x, 0, this.playerVelocity.z);
    rotatedVelocity.applyMatrix4(rotationMatrix);
    this.playerVelocity.x = rotatedVelocity.x;
    this.playerVelocity.z = rotatedVelocity.z;
    
    // Calculate the potential new position
    const newPosition = new THREE.Vector3().copy(this.localPlayer.position).add(this.playerVelocity);
    
    // Adjust Y position based on terrain height if we have an island map
    if (!this.isJumping && this.mapScene && typeof this.mapScene.getTerrainHeightAt === 'function') {
      const terrainHeight = this.mapScene.getTerrainHeightAt(newPosition.x, newPosition.z);
      if (terrainHeight !== undefined) {
        // Add a small offset to prevent clipping with the terrain
        newPosition.y = terrainHeight + TERRAIN_Y_OFFSET;
      }
    }
    
    // Track if position has changed
    let positionChanged = false;
    
    // Check for collisions with walls and obstacles
    if (!this.checkCollisions(newPosition)) {
      // If no collision, update player position
      this.localPlayer.position.copy(newPosition);
      positionChanged = true;
      
      // Update camera position to follow player
      const cameraHeightOffset = PLAYER_EYE_HEIGHT; // Eye height from constant
      this.camera.position.set(
        this.localPlayer.position.x,
        this.localPlayer.position.y + cameraHeightOffset,
        this.localPlayer.position.z + 0.1
      );
      
      // Send position update to server if we have a socket connection
      if (this.socket && positionChanged) {
        this.sendPositionUpdate();
      }
    } else {
      // If collision detected, try moving only along non-blocked axes
      // Try X movement
      const xOnlyPosition = new THREE.Vector3().copy(this.localPlayer.position);
      xOnlyPosition.x += this.playerVelocity.x;
      
      // Adjust Y position for X-only movement
      if (this.mapScene && typeof this.mapScene.getTerrainHeightAt === 'function') {
        const terrainHeight = this.mapScene.getTerrainHeightAt(xOnlyPosition.x, xOnlyPosition.z);
        if (terrainHeight !== undefined) {
          xOnlyPosition.y = terrainHeight + TERRAIN_Y_OFFSET;
        }
      }
      
      if (!this.checkCollisions(xOnlyPosition)) {
        this.localPlayer.position.copy(xOnlyPosition);
        positionChanged = true;
      }
      
      // Try Z movement
      const zOnlyPosition = new THREE.Vector3().copy(this.localPlayer.position);
      zOnlyPosition.z += this.playerVelocity.z;
      
      // Adjust Y position for Z-only movement
      if (this.mapScene && typeof this.mapScene.getTerrainHeightAt === 'function') {
        const terrainHeight = this.mapScene.getTerrainHeightAt(zOnlyPosition.x, zOnlyPosition.z);
        if (terrainHeight !== undefined) {
          zOnlyPosition.y = terrainHeight + TERRAIN_Y_OFFSET;
        }
      }
      
      if (!this.checkCollisions(zOnlyPosition)) {
        this.localPlayer.position.copy(zOnlyPosition);
        positionChanged = true;
      }
      
      // Update camera position to follow player
      if (positionChanged) {
        const cameraHeightOffset = PLAYER_EYE_HEIGHT; // Eye height from constant
        this.camera.position.set(
          this.localPlayer.position.x,
          this.localPlayer.position.y + cameraHeightOffset,
          this.localPlayer.position.z + 0.1
        );
        
        // Send position update to server if we have a socket connection
        if (this.socket) {
          this.sendPositionUpdate();
        }
      } else {
        console.log("Collision detected, can't move to: " + newPosition.x.toFixed(2) + ", " + newPosition.y.toFixed(2) + ", " + newPosition.z.toFixed(2));
      }
    }
    
    // Update player animations based on movement
    this.updatePlayerAnimation();
    
    // Handle crouch camera height transition
    if (this.cameraHeightTarget !== undefined && this.cameraHeightCurrent !== undefined) {
      // Use a faster transition for more responsive feel
      const transitionSpeed = 0.4; // Increased from 0.2 for faster response
      
      // Smoothly transition camera height when crouching/standing
      const timeSinceCrouchToggle = performance.now() / 1000 - this.lastCrouchToggleTime;
      const t = Math.min(timeSinceCrouchToggle * transitionSpeed, 1);
      
      // Calculate new current height using lerp
      this.cameraHeightCurrent = THREE.MathUtils.lerp(
        this.cameraHeightCurrent,
        this.cameraHeightTarget,
        t
      );
      
      // Safety check - ensure height doesn't go below minimum
      if (this.cameraHeightCurrent < 0.1) {
        this.cameraHeightCurrent = 0.1;
      }
      
      // Directly set the camera height based on player position and current height
      if (this.camera && this.localPlayer) {
        // Ensure we're using localPlayer's position for the camera
        this.camera.position.y = this.localPlayer.position.y + this.cameraHeightCurrent;
      }
      
      // Debug log camera height occasionally (not every frame)
     
    }
  }
  
  // Send position and rotation data to the server
  sendPositionUpdate(force = false) {
    // Track if this is a movement state change (start/stop moving)
    const isMoving = this.keys.forward || this.keys.backward || this.keys.left || this.keys.right;
    
    // Check if movement state changed since last update
    if (!this.lastMovementState) {
      this.lastMovementState = { 
        isMoving,
        time: Date.now()
      };
    }
    
    const movementStateChanged = this.lastMovementState.isMoving !== isMoving;
    
    // Update movement state tracking
    if (movementStateChanged) {
      this.lastMovementState = {
        isMoving,
        time: Date.now()
      };
    }
    
    // Send update immediately if:
    // 1. Force is true, OR
    // 2. Movement state changed (started/stopped moving), OR
    // 3. It's been more than 50ms since last update
    const shouldUpdate = force || 
                         movementStateChanged ||
                         !this.lastPositionUpdateTime || 
                         (Date.now() - this.lastPositionUpdateTime > 50);
    
    if (shouldUpdate && this.socket && this.localPlayer) {
      this.lastPositionUpdateTime = Date.now();
      
      // Ensure isCrouching is included in every update
      const updateData = {
        position: {
          x: this.localPlayer.position.x,
          y: this.localPlayer.position.y,
          z: this.localPlayer.position.z
        },
        rotation: {
          y: this.cameraRotation.y
        },
        isCrouching: this.isCrouching,
        isJumping: this.isJumping,
        jumpVelocity: this.jumpVelocity,
        isMoving: isMoving // Explicitly include movement state
      };
      
      // Send movement state changes with higher priority
      if (movementStateChanged) {
        console.log(`Movement state changed: ${this.lastMovementState.isMoving ? 'moving' : 'stopped'}`);
        updateData.movementStateChanged = true;
      }
      
      this.socket.emit('position-update', updateData);
    }
  }
  
  // Check for collisions between the player and walls/obstacles
  checkCollisions(position) {
    if (!this.mapScene) return false;
    
    // Initialize collision result - will be set to true if any collision is detected
    let collision = false;
    
    // Check if the position is in deep water - prevent players from entering it
    if (this.mapScene.isInDeepWater && typeof this.mapScene.isInDeepWater === 'function') {
      try {
        // Skip water check if the debug flag is set (for testing)
        if (this.ignoreWaterCollision) {
          // Special debug mode to bypass water collision
        } else {
          // Track previous water check result for the current position to avoid oscillation
          if (!this.lastWaterCheck) {
            this.lastWaterCheck = {
              position: new THREE.Vector3(),
              result: false,
              time: 0
            };
          }
          
          // If we recently checked a similar position, reuse the result to avoid jitter
          const distToLastCheck = position.distanceTo(this.lastWaterCheck.position);
          const timeSinceLastCheck = Date.now() - this.lastWaterCheck.time;
          
          if (distToLastCheck < 0.1 && timeSinceLastCheck < 500) {
            if (this.lastWaterCheck.result) {
              // Only return if water was detected - let other collision checks happen otherwise
              return true;
            }
          } else {
            // Perform the actual water check
            const isWater = this.mapScene.isInDeepWater(position.x, position.z);
            
            // Store this check for future reference
            this.lastWaterCheck.position.copy(position);
            this.lastWaterCheck.result = isWater;
            this.lastWaterCheck.time = Date.now();
            
            if (isWater) {
              // Only log occasionally to avoid spam
              if (Math.random() < 0.05) {
                console.log("Water boundary at:", position.x.toFixed(2), position.z.toFixed(2));
              }
              return true; // Collision with water - return immediately
            }
          }
        }
      } catch (error) {
        console.error("Error during water collision check:", error);
      }
    }
    
    // Get all map objects for collision testing
    const mapObjects = this.mapScene.getMapObjects();
    if (!mapObjects || mapObjects.length === 0) return false;
    
    // Get character config for proper hitbox size
    let characterType = 'soldier'; // Default
    let characterScale = 1.0;
    let playerWidth = 0.5;     // Default width
    let playerDepth = 0.4;     // Default depth
    let playerHeight = 1.2;    // Default height
    
    // Use local player's character type if available
    if (this.localPlayer && this.localPlayer.userData) {
      characterType = this.localPlayer.userData.character || 'soldier';
    }
    
    // Get character configuration
    if (this.characterModels && this.characterModels[characterType]) {
      const characterConfig = this.characterModels[characterType];
      characterScale = characterConfig.scale || 1.0;
      playerHeight = characterConfig.height || 1.2;
      
      // Update dimensions based on character model and scale
      playerWidth = 0.5 * characterScale;     // Left-right width
      playerDepth = 0.4 * characterScale;     // Front-back depth
    }
    
    // Check each object for collision
    for (const object of mapObjects) {
      // Skip objects without geometry (like lights)
      if (!object.geometry && (!object.children || object.children.length === 0)) continue;
      
      // Skip visual indicators (like spawn points) that shouldn't cause collisions
      if (object.userData && object.userData.isVisualIndicator) continue;
      
      // Skip floor objects to allow player movement
      if (object.userData && object.userData.isMap && object.rotation && 
          Math.abs(object.rotation.x + Math.PI/2) < 0.1) {
        // This is a floor (rotated -90 degrees around X axis)
        continue;
      }
      
      // Handle Group objects with children (like rock formations)
      if (object.isGroup || object.children?.length > 0) {
        // Check if this is an obstacle group
        if (object.userData && object.userData.isObstacle) {
          // Check collision with each child in the group that has geometry
          let childCollision = false;
          
          object.traverse(child => {
            if (child.isMesh && child.geometry) {
              // Skip if this isn't the direct check and child is not marked as obstacle
              if (child !== object && child.userData && !child.userData.isObstacle) {
                return;
              }
              
              // Create bounds for this child
              if (!child.geometry.boundingBox) {
                child.geometry.computeBoundingBox();
              }
              
              // Get world bounds
              const childBounds = child.geometry.boundingBox.clone();
              childBounds.applyMatrix4(child.matrixWorld);
              
              // Create player bounds
              const playerBounds = new THREE.Box3(
                new THREE.Vector3(
                  position.x - playerWidth / 2,
                  position.y,
                  position.z - playerDepth / 2
                ),
                new THREE.Vector3(
                  position.x + playerWidth / 2,
                  position.y + playerHeight,
                  position.z + playerDepth / 2
                )
              );
              
              // Check for intersection
              if (playerBounds.intersectsBox(childBounds)) {
                // Log collision details
                console.log("Child collision detected with object type:", 
                           child.userData.type || "unknown",
                           "in group:", object.userData.type || "unknown");
                childCollision = true;
              }
            }
          });
          
          if (childCollision) {
            return true; // Collision detected with a child
          }
        }
        
        // If it's just a regular group without the obstacle flag, continue to next object
        if (!object.geometry) continue;
      }
      
      // For regular objects with geometry, proceed with normal collision check
      // Get the object bounds
      if (!object.geometry.boundingBox) {
        object.geometry.computeBoundingBox();
      }
      
      // Create a bounding box for the object in world space
      const objectBounds = object.geometry.boundingBox.clone();
      objectBounds.applyMatrix4(object.matrixWorld);
      
      // Create player bounds as a simple box (we could use a capsule, but box is simpler)
      // Since position is at the bottom of the player, we need to adjust for player height
      const playerBounds = new THREE.Box3(
        new THREE.Vector3(
          position.x - playerWidth / 2,
          position.y,                   // Bottom of player
          position.z - playerDepth / 2
        ),
        new THREE.Vector3(
          position.x + playerWidth / 2,
          position.y + playerHeight,    // Top of player
          position.z + playerDepth / 2
        )
      );
      
      // Check for box-box intersection
      if (playerBounds.intersectsBox(objectBounds)) {
        console.log("Collision detected with object:", object.userData, 
                   "using character:", characterType, 
                   "with dimensions:", playerWidth.toFixed(2), "x", 
                   playerHeight.toFixed(2), "x", playerDepth.toFixed(2));
        return true; // Collision detected
      }
      
      // Additional sphere-based check for diagonal movement (smoother collision)
      // This helps prevent getting stuck on corners
      const playerCenter = new THREE.Vector3(
        position.x,
        position.y + playerHeight / 2, // Middle of player height
        position.z
      );
      
      // Use minimum of width/depth for radius to prevent over-collision
      const playerRadius = Math.min(playerWidth, playerDepth) / 2;
      
      // Calculate closest point on box to sphere center
      const closestPoint = new THREE.Vector3().copy(playerCenter);
      
      // Clamp the point to the box bounds
      closestPoint.x = Math.max(objectBounds.min.x, Math.min(objectBounds.max.x, closestPoint.x));
      closestPoint.y = Math.max(objectBounds.min.y, Math.min(objectBounds.max.y, closestPoint.y));
      closestPoint.z = Math.max(objectBounds.min.z, Math.min(objectBounds.max.z, closestPoint.z));
      
      // If the distance from the closest point to the sphere center is less than the radius, we have a collision
      const distance = playerCenter.distanceTo(closestPoint);
      if (distance < playerRadius) {
        console.log("Sphere collision detected with object:", object.userData, 
                   "at distance:", distance.toFixed(2),
                   "using radius:", playerRadius.toFixed(2));
        return true; // Collision detected
      }
    }
    
    return false; // No collision
  }
  
  // Initialize weapon system
  initWeaponSystem() {
    console.log("Initializing weapon system...");
    
    if (!this.scene || !this.camera) {
      console.error('Cannot initialize weapon system: scene or camera not available');
      return;
    }
    
    try {
      // If already initialized, don't recreate
      if (this.weaponSystem) {
        console.log('Weapon system already initialized, dispatching ready event');
        // Make sure the ready event is dispatched even if already initialized
        this._dispatchWeaponSystemReadyEvent();
        return;
      }
      
      // FORCE INITIALIZATION - don't wait for weapons-loaded event
      // This helps ensure we don't miss the event
      console.log('FORCING weapon system initialization without waiting for event');
      this._initializeWeaponSystemWithSocket();
      
      // Still listen for the event as a backup
      const weaponsLoadedHandler = (event) => {
        console.log('Received weapons-loaded event:', event.detail);
        
        // Remove this event listener to avoid multiple initializations
        window.removeEventListener('weapons-loaded', weaponsLoadedHandler);
        
        // Update weapons if the system is already initialized
        if (this.weaponSystem) {
          console.log('Weapon system already initialized, updating weapons');
          
          // Make sure the weapon appears on all players
          this._updateAllPlayerWeapons();
        } else {
          // Now initialize the weapon system with loaded weapon models
          console.log('Initializing weapon system from event handler');
          this._initializeWeaponSystemWithSocket();
        }
      };
      
      // Listen for weapon loaded event (as backup)
      window.addEventListener('weapons-loaded', weaponsLoadedHandler);
      
    } catch (error) {
      console.error('Error initializing weapon system:', error);
    }
  }
  
  // Helper method to update weapons on all players
  _updateAllPlayerWeapons() {
    console.log('Updating weapons on all players');
    
    if (!this.weaponSystem || !this.playerManager) {
      console.warn('Cannot update player weapons: weapon system or player manager not available');
      return;
    }
    
    const currentWeaponType = this.getWeaponModelType(this.weaponSystem.currentWeapon);
    console.log(`Current weapon type: ${currentWeaponType}`);
    
    // Update other players
    if (this.playerManager.players) {
      const playerIds = Object.keys(this.playerManager.players);
      console.log(`Updating weapons for ${playerIds.length} players`);
      
      Object.values(this.playerManager.players).forEach(player => {
        this.playerManager.attachWeaponToPlayer(player, currentWeaponType);
      });
    } else {
      console.log('No other players to update weapons for');
    }
    
    // Update local player
    if (this.playerManager.localPlayer) {
      console.log('Updating weapon for local player');
      this.playerManager.attachWeaponToPlayer(
        this.playerManager.localPlayer, 
        currentWeaponType
      );
    } else {
      console.log('No local player to update weapon for');
    }
  }
  
  // Helper method to initialize weapon system with proper socket handling
  _initializeWeaponSystemWithSocket() {
    // Check if socket is available and connected
    if (this.socket) {
      console.log(`Socket status when initializing weapon system - ID: ${this.socket.id}, Connected: ${this.socket.connected}`);
      
      // If socket exists but isn't connected yet, set up a connection handler
      if (!this.socket.connected) {
        console.log('Socket not connected yet, setting up connection event for weapon system');
        
        // Set a one-time event handler for when the socket connects
        this.socket.once('connect', () => {
          console.log(`Socket now connected (ID: ${this.socket.id}), initializing weapon system with connected socket`);
          this._createWeaponSystem();
        });
        
        // Proceed with initialization anyway, the socket will be updated when connected
        this._createWeaponSystem();
      } else {
        // Socket is already connected, proceed normally
        this._createWeaponSystem();
      }
    } else {
      // No socket available, create weapon system without socket
      console.warn('Initializing weapon system without socket connection');
      this._createWeaponSystem();
    }
  }
  
  // Helper method to create the weapon system
  _createWeaponSystem() {
    // Create weapon system with socket reference and local player ID
    this.weaponSystem = markRaw(new WeaponSystem(this.scene, this.camera, this.socket));
    
    // Initialize weapon system with current player death state
    if (this.isDead !== undefined) {
      this.weaponSystem.setPlayerDeathState(this.isDead);
    }
    
    // Pre-initialize audio context to allow spatial audio
    // This is important because browsers require user interaction
    // before audio contexts can be created
    window.addEventListener('click', () => {
      if (this.weaponSystem && !this.weaponSystem.soundSystem.audioContext) {
        try {
          // Initialize audio context on first user interaction
          this.weaponSystem.soundSystem.audioContext = new (window.AudioContext || window.webkitAudioContext)();
          console.log('Audio context initialized for spatial audio');
        } catch (e) {
          console.warn('Failed to initialize audio context:', e);
        }
      }
    }, { once: true });
    
    // Set up additional mouse events for automatic fire
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
    
    // Connect weapon system to player manager for third-person views
    if (this.playerManager) {
      // Handle weapon switching to update player models
      this.weaponSystem.onWeaponSwitch = (weaponType) => {
        console.log(`Handling weapon switch to ${weaponType}`);
        // Update the weapon on player models when switching weapons
        if (this.playerManager.players) {
          Object.values(this.playerManager.players).forEach(player => {
            this.playerManager.attachWeaponToPlayer(player, this.getWeaponModelType(weaponType));
          });
        }
        
        // Also update local player if available
        if (this.playerManager.localPlayer) {
          this.playerManager.attachWeaponToPlayer(
            this.playerManager.localPlayer, 
            this.getWeaponModelType(weaponType)
          );
        }
      };
      
      // Initial weapon setup - set current weapon on all players
      const initialWeaponModelType = this.getWeaponModelType(this.weaponSystem.currentWeapon);
      console.log(`Setting initial weapon (${initialWeaponModelType}) on players`);
      
      if (this.playerManager.players) {
        Object.values(this.playerManager.players).forEach(player => {
          this.playerManager.attachWeaponToPlayer(player, initialWeaponModelType);
        });
      }
      
      // Make sure local player has weapon
      if (this.playerManager.localPlayer) {
        this.playerManager.attachWeaponToPlayer(
          this.playerManager.localPlayer, 
          initialWeaponModelType
        );
      }
    } else {
      console.warn('PlayerManager not available, cannot set up weapon models on players');
    }
    
    // Dispatch weapon system ready event
    this._dispatchWeaponSystemReadyEvent();
    
    console.log('Weapon system fully initialized and ready');
  }
  
  // Helper method to dispatch the weapon system ready event
  _dispatchWeaponSystemReadyEvent() {
    // Dispatch an event that the weapon system is ready
    const weaponSystemReadyEvent = new CustomEvent('weapon-system-ready', {
      detail: {
        weaponType: this.weaponSystem ? this.weaponSystem.currentWeapon : null
      }
    });
    window.dispatchEvent(weaponSystemReadyEvent);
    console.log('Dispatched weapon-system-ready event');
  }
  
  // Helper to map weapon types to model types
  getWeaponModelType(weaponType) {
    console.log(`Mapping weapon type ${weaponType} to model type`);
    
    // Map weapon types to model types
    const modelTypeMap = {
      'knife': 'knife',
      'pistol': 'pistol',
      'machineGun': 'rifle'  // This maps the machineGun weapon type to the rifle model
    };
    
    const modelType = modelTypeMap[weaponType] || 'rifle'; // Default to rifle if not found
    console.log(`Mapped to model type: ${modelType}`);
    return modelType;
  }
  
  // Create gun shot visual effect  
  createGunShotVisual(playerMesh, position, direction) {
    this.playerManager.createGunShotVisual(playerMesh, position, direction);
  }
  
  // Create gun shot visual at a specific position (without needing a player mesh)
  createGunShotVisualAtPosition(position, direction) {
    this.playerManager.createGunShotVisualAtPosition(position, direction);
  }
  
  // Create visual effect for melee attack from another player
  createMeleeAttackVisual(playerMesh) {
    this.playerManager.createMeleeAttackVisual(playerMesh);
  }
  
  // Create hit effect at the impact point with body part consideration
  createHitEffect(position, bodyPart = 'default') {
    this.playerManager.createHitEffect(position, bodyPart);
  }
  
  // Create a special effect for headshots
  createHeadshotEffect(position) {
    this.playerManager.createHeadshotEffect(position);
  }
  
  // Create respawn effect at a specific position
  createRespawnEffect(position) {
    this.playerManager.createRespawnEffect(position);
  }
  
  // Helper method to add visual indication of spawn protection
  updateSpawnProtectionVisual(isProtected) {
    this.playerManager.updateSpawnProtectionVisual(isProtected);
  }
  
  // Helper method to add spawn protection visual effect to other players
  addSpawnProtectionEffect(playerMesh) {
    this.playerManager.addSpawnProtectionEffect(playerMesh, true);
  }
  
  // Add a method to show damage direction indicator
  showDamageDirectionIndicator(attackerPosition) {
    this.playerManager.showDamageDirectionIndicator(attackerPosition);
  }
  
  // Show headshot feedback for local player (screen effects)
  showHeadshotFeedback() {
    // This would typically be implemented in the UI layer
    // Here we'll just dispatch an event for the UI to handle
    const headshotEvent = new CustomEvent('player-headshot-hit', {
      detail: { timestamp: Date.now() }
    });
    window.dispatchEvent(headshotEvent);
  }

  /**
   * Validates a spawn position to ensure it doesn't collide with obstacles
   * @param {THREE.Vector3} position - The position to validate
   * @returns {boolean} - True if the position is valid (no collisions), false otherwise
   */
  validateSpawnPosition(position) {
    // Skip validation if there's no map scene
    if (!this.mapScene) return true;
    
    // First check if position is in deep water
    if (this.mapScene.isInDeepWater && typeof this.mapScene.isInDeepWater === 'function') {
      if (this.mapScene.isInDeepWater(position.x, position.z)) {
        console.log("Invalid spawn position: in deep water");
        return false;
      }
    }
    
    // Define a buffer distance around the spawn point to check
    const bufferDistance = 1.0; // 1 meter buffer
    
    // Check the central position
    if (this.checkCollisions(position)) {
      console.log("Invalid spawn position: collision at center point");
      return false;
    }
    
    // Check additional points around the player to ensure there's room to move
    const testPoints = [
      new THREE.Vector3(position.x + bufferDistance, position.y, position.z), // Right
      new THREE.Vector3(position.x - bufferDistance, position.y, position.z), // Left
      new THREE.Vector3(position.x, position.y, position.z + bufferDistance), // Forward
      new THREE.Vector3(position.x, position.y, position.z - bufferDistance)  // Backward
    ];
    
    // Check each test point
    for (const point of testPoints) {
      // Adjust Y position for terrain height
      if (this.mapScene && typeof this.mapScene.getTerrainHeightAt === 'function') {
        const terrainHeight = this.mapScene.getTerrainHeightAt(point.x, point.z);
        if (terrainHeight !== undefined) {
          point.y = Math.max(point.y, terrainHeight + TERRAIN_Y_OFFSET);
        }
      }
      
      // Check if the point collides with any obstacles
      if (this.checkCollisions(point)) {
        console.log("Invalid spawn position: collision at buffer point", point);
        return false;
      }
      
      // Also check water for this point
      if (this.mapScene.isInDeepWater && typeof this.mapScene.isInDeepWater === 'function') {
        if (this.mapScene.isInDeepWater(point.x, point.z)) {
          console.log("Invalid spawn position: buffer point in deep water", point);
          return false;
        }
      }
    }
    
    // All checks passed, position is valid
    console.log("Valid spawn position confirmed", position);
    return true;
  }
  
  // Method to update hitbox visualizations
  updateHitboxVisualizations() {
    if (this.playerManager) {
      this.playerManager.updateHitboxVisualizations();
    }
  }
  
  // Method to clear all hitbox visualizations
  clearHitboxVisualizations() {
    if (this.playerManager) {
      this.playerManager.clearHitboxVisualizations();
    }
  }

  // Helper method to determine if a player is moving
  isPlayerMoving(playerMesh) {
    if (!playerMesh || !playerMesh.userData || !playerMesh.userData.lastPosition) {
      return false;
    }
    
    const currentPos = playerMesh.position;
    const lastPos = playerMesh.userData.lastPosition;
    
    // Calculate distance moved
    const dx = currentPos.x - lastPos.x;
    const dz = currentPos.z - lastPos.z;
    const distance = Math.sqrt(dx*dx + dz*dz);
    
    // Use a small threshold to detect meaningful movement
    const movementThreshold = 0.01;
    return distance > movementThreshold;
  }
  
  // Configure animation blending for smoother transitions
  setAnimationBlendSettings(options) {
    if (this.playerManager) {
      this.playerManager.setAnimationBlendDurations(options);
      return true;
    }
    return false;
  }
  
  /**
   * Configure animation system with comprehensive settings
   * @param {Object} config - Configuration object with animation settings
   * @returns {boolean} - Whether configuration was applied successfully
   */
  setAnimationConfig(config = {}) {
    // Default values
    const defaultConfig = {
      blendDurations: {
        default: 0.15,     // General transitions
        directional: 0.12, // Direction changes
        running: 0.08,     // Running transitions
        idle: 0.1,         // Idle transitions
        movement: 0.05     // Movement state changes
      },
      lockTimeFactor: 0.2, // How much of blend time to lock (0.2 = 20%)
      movementThreshold: 0.02, // Threshold for detecting movement changes
      debug: false         // Whether to show debug logs
    };

    // Merge with defaults
    const finalConfig = {...defaultConfig, ...config};
    
    // Apply to animation controller via PlayerManager
    if (this.playerManager) {
      const result = this.playerManager.setAnimationConfig(finalConfig);
      console.log('Animation configuration updated:', finalConfig);
      return result;
    }
    
    return false;
  }
  
  // Configure position interpolation factor
  setInterpolationFactor(factor) {
    if (this.playerManager) {
      return this.playerManager.setInterpolationFactor(factor);
    }
    return false;
  }
  
  // Get current animation and interpolation settings
  getMovementSmoothingSettings() {
    if (!this.playerManager) return null;
    
    return {
      interpolation: {
        factor: this.playerManager.interpolationFactor
      },
      animation: {
        defaultBlend: this.playerManager.defaultBlendDuration,
        directionalBlend: this.playerManager.directionalBlendDuration,
        runningBlend: this.playerManager.runningBlendDuration,
        idleBlend: this.playerManager.idleBlendDuration
      }
    };
  }
  
  // Add this method to log player pool statistics
  setupPoolStatsLogger() {
    // Log player pool statistics every 30 seconds
    this.poolStatsInterval = setInterval(() => {
      if (this.playerManager && this.playerManager.playerPools) {
        const { soldier } = this.playerManager.playerPools;
        if (soldier) {
          console.log(`==== Player Pool Stats ====`);
          console.log(`Available models: ${soldier.availableCount}`);
          console.log(`In-use models: ${soldier.inUseCount}`);
          console.log(`Total models: ${soldier.availableCount + soldier.inUseCount}`);
          console.log(`==========================`);
        }
      }
    }, 30000); // 30 seconds
  }
  
  // Add a method to get pool stats on demand (can be called from console)
  getPlayerPoolStats() {
    if (!this.playerManager || !this.playerManager.playerPools || !this.playerManager.playerPools.soldier) {
      return { error: 'Player pool not initialized' };
    }
    
    const pool = this.playerManager.playerPools.soldier;
    return {
      availableCount: pool.availableCount,
      inUseCount: pool.inUseCount,
      totalCount: pool.availableCount + pool.inUseCount,
      isInitialized: pool.isInitialized
    };
  }

  // Display help for profiling shortcuts
  displayProfilingHelp() {
    console.log('%c=== HIT DETECTION PROFILING HELP ===', 'font-weight: bold; font-size: 14px; color: #4f4');
    console.log('%cKeyboard Shortcuts:', 'font-weight: bold; color: #ff8');
    
    console.log('%cStatistics:', 'font-weight: bold');
    console.log('  P:       Show hit detection statistics and optimization suggestions');
    console.log('  F1:      Display this help message');
    
    console.log('%cTarget Caching:', 'font-weight: bold');
    console.log('  T:       Toggle target caching on/off');
    console.log('  Shift+T: Increase target cache update interval (+100ms)');
    console.log('  Ctrl+T:  Decrease target cache update interval (-100ms)');
    
    console.log('%cDebug Visualization:', 'font-weight: bold');
    console.log('  H:       Toggle hitbox visualization');
    
    console.log('%cHow to Profile:', 'font-weight: bold; color: #4af');
    console.log('1. Press P to see current hit detection statistics');
    console.log('2. Test with target caching ON/OFF (T key) to compare performance');
    console.log('3. Adjust cache interval (Shift+T, Ctrl+T) to find optimal balance');
    console.log('4. Toggle hitbox visualization (H key) to see what\'s being hit');
    console.log('5. Check the console for detailed timing of each shot');
    
    console.log('%cPerformance Tips:', 'font-weight: bold; color: #fa4');
    console.log('- Higher cache intervals reduce CPU usage but may miss new objects');
    console.log('- Scene traversal is usually the most expensive operation');
    console.log('- Consider implementing an Octree for larger scenes');
    console.log('- Pre-compute collision meshes for complex character models');
    
    console.log('%c=======================================', 'font-weight: bold; color: #4f4');
  }

  // Helper method to get ground level at a position
  getGroundLevel(x, z) {
    // Use terrain height if available
    if (this.mapScene && typeof this.mapScene.getTerrainHeightAt === 'function') {
      const terrainHeight = this.mapScene.getTerrainHeightAt(x, z);
      if (terrainHeight !== undefined) {
        return terrainHeight + TERRAIN_Y_OFFSET;
      }
    }
    // Default ground level of 0
    return TERRAIN_Y_OFFSET || 0;
  }

  // Add a method to toggle animation debugging
  toggleAnimationDebug() {
    if (this.playerManager && this.playerManager.animationController) {
      const debugMode = !this.playerManager.animationController.debug;
      this.playerManager.animationController.setDebug(debugMode);
      console.log(`Animation debugging ${debugMode ? 'enabled' : 'disabled'}`);
      return debugMode;
    }
    return false;
  }
  
  // Set camera to bird's eye view of the map
  setBirdsEyeView() {
    const mapBounds = this.getMapBounds();
    const centerX = (mapBounds.max.x + mapBounds.min.x) / 2;
    const centerZ = (mapBounds.max.z + mapBounds.min.z) / 2;
    
    // Position camera high above the map center
    const height = Math.max(
      mapBounds.max.x - mapBounds.min.x,
      mapBounds.max.z - mapBounds.min.z
    ) * 0.8; // Height proportional to map size
    
    this.camera.position.set(centerX, height, centerZ);
    this.camera.lookAt(centerX, 0, centerZ);
    this.camera.rotation.z = 0; // Keep the camera level
    
    console.log('Setting birds-eye view camera position:', this.camera.position);
  }
  
  // Get the bounds of the map for positioning the bird's eye view
  getMapBounds() {
    if (this.mapScene && this.mapScene.getMapBounds) {
      return this.mapScene.getMapBounds();
    }
    
    // Default bounds if mapScene doesn't provide them
    return {
      min: new THREE.Vector3(-50, 0, -50),
      max: new THREE.Vector3(50, 10, 50)
    };
  }
  
  // Start transition from bird's eye view to player view
  startCameraTransition(playerPosition) {
    // Skip transition and go directly to first-person view
    console.log('Skipping camera transition - going directly to first-person view');
    
    // Set camera directly to first-person position
    this.camera.position.set(
      playerPosition.x,
      playerPosition.y + this.PLAYER_EYE_HEIGHT,
      playerPosition.z
    );
    
    // Set camera rotation
    this.camera.rotation.copy(this.cameraRotation);
    
    // Mark transition as complete
    this.cameraTransitioning = false;
    this.playerJoined = true;
    
    // Setup first-person controls
    this.setupFirstPersonCamera();
  }
  
  // Update camera transition in animation loop
  updateCameraTransition() {
    // No transition needed - we're skipping it
    return;
  }
  
  // Easing function for smoother camera transitions
  easeInOutCubic(t) {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  
  // Set up first-person camera after transition
  setupFirstPersonCamera() {
    if (!this.localPlayer) {
      console.error('Cannot setup first-person camera: localPlayer not found');
      return;
    }
    
    // Reset camera properties for first-person control
    this.camera.position.set(
      this.localPlayer.position.x,
      this.localPlayer.position.y + this.PLAYER_EYE_HEIGHT,
      this.localPlayer.position.z
    );
    
    // Reset camera rotation
    this.cameraRotation.set(0, 0, 0, 'YXZ');
    this.camera.rotation.copy(this.cameraRotation);
    
    // Enable mouse controls
    this.enableMouseControls();
  }
  
  // Player joins the game
  joinGame(username) {
    if (this.playerJoined) {
      return; // Already joined
    }
    
    // Emit join event to server with username
    if (this.socket && this.socket.connected) {
      this.socket.emit('player-join', { username });
      console.log('Sent player-join event to server with username:', username);
    } else {
      console.error('Cannot join game: socket not connected');
    }
  }
}

export default MainScene;